import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import axios from 'axios';
import s from './PostAProject.css';
import lpApi from '../../helpers/lpApi';
import qrSvg from '../../helpers/qrSvg';
import history from '../../history';
import { sendVerificationSecureSms, verifyPhoneNumber, createUser } from '../../lib/graphql';
import { getCookies, setCookies } from '../../helpers/Cookies/cookies';
import { encode } from '../../helpers/queryEncryption';
import { clientSmsKey } from '../../helpers/clientSmsKey';

// ============================================================================
// "Post a Project" — client-side Custom Projects intake wizard (WEB ONLY).
//
// SMALL-JOBS / LARGE-PROJECTS SEPARATION (founder requirement): this wizard is
// built ENTIRELY on its own large-project category enum + static intake. It does
// NOT read the small-jobs service catalog (Section / Category / SubCategory /
// CategoryField, i.e. customProjectSections / categoryIntakeQuestions). That fusion
// was removed so a small job (Cleaning, TV Mounting, …) can never become a large-
// project request. The two flows must never be confused.
//
// Premium multi-step flow:
//   1. Pick a category  — LP-NATIVE tiles, one per createProjectRequest enum value
//                         (KITCHEN / BATHROOM / BASEMENT / EXTERIOR / ROOFING /
//                         INSTALLATION / OTHER). The tile's value IS the enum sent
//                         to the API — a 1:1 map (LP_CATEGORIES).
//   2. Intake           — LP-NATIVE static sub-option chips (LP_CATEGORIES[].subs)
//                         + a free-text scope note. No small-jobs CategoryField.
//   3..5 Photos / Measurements / Budget+timeline+location — universal LP fields.
//   6. Review & submit  — createProjectRequest (DRAFT) then updateProjectRequest
//                         with the LP fields, then a success state.
//
// AUTH MODEL (Paul): a LOGGED-OUT client can do EVERYTHING in the wizard (the
// step-1 catalog is a static local list — no fetch). Auth is enforced ONLY at the
// final SUBMIT (createProjectRequest / updateProjectRequest). When the user taps
// "Post my project":
//   - if an `id_token` cookie is present  -> submit directly.
//   - if NOT signed in                    -> show an inline auth step (the SAME
//     phone + SMS-OTP mechanism the rest of the web uses:
//     sendVerificationSecureSms -> verifyPhoneNumber -> createUser, mirrored from
//     CompanyAuth.js and src/actions/login/*). On success we drop the `id_token`
//     cookie and immediately run the real submit. The wizard's filled answers stay
//     in component state across the auth step — nothing is lost, no reload.
//
// All backend operations live on besthand-api and are reached through the
// `/lp-graphql` server proxy via the `lpApi` helper — no LP resolver is
// duplicated in besthand-web. Photos upload to the existing /uploadCompanyDocument
// route (returns an absolute documentUrl), exactly like the company wizard.
//
// SSR-safe: window / FormData / fetch / cookies are only touched inside lifecycle
// and event handlers, never at construction or render.
// ============================================================================

// ---- GraphQL operations (shape mirrors besthand-api Custom Projects queries) ----
//
// NOTE (small-jobs / large-projects SEPARATION): this LP "Post a Project" wizard
// is deliberately built on its OWN large-project category enum (LP_CATEGORIES,
// below) and its OWN static intake fields (LP_INTAKE_FIELDS). It does NOT read the
// small-jobs service catalog (Section / Category / SubCategory / CategoryField via
// customProjectSections / categoryIntakeQuestions). Those reads were removed so a
// small job (Cleaning, TV Mounting, …) can never be turned into a large-project
// request. The two flows must never be confused.

const M_CREATE = `
  mutation (
    $category: String!, $siteAddressLine1: String!, $siteCity: String!,
    $siteState: String!, $siteZip: String!, $title: String
  ) {
    createProjectRequest(
      category: $category, siteAddressLine1: $siteAddressLine1, siteCity: $siteCity,
      siteState: $siteState, siteZip: $siteZip, title: $title
    ) {
      status errorMessage
      projectRequest { id status category subcategory title }
    }
  }
`;

const M_UPDATE = `
  mutation (
    $projectRequestId: String!, $subcategory: String, $description: String,
    $propertyType: String, $ambitionLevel: String,
    $budgetMin: Float, $budgetMax: Float, $timelineJson: String,
    $categorySpecificDataJson: String, $intakeAnswersJson: String,
    $measurementsJson: String, $existingConditionsJson: String,
    $proPreferencesJson: String
  ) {
    updateProjectRequest(
      projectRequestId: $projectRequestId, subcategory: $subcategory,
      description: $description, propertyType: $propertyType, ambitionLevel: $ambitionLevel,
      budgetMin: $budgetMin, budgetMax: $budgetMax,
      timelineJson: $timelineJson, categorySpecificDataJson: $categorySpecificDataJson,
      intakeAnswersJson: $intakeAnswersJson, measurementsJson: $measurementsJson,
      existingConditionsJson: $existingConditionsJson, proPreferencesJson: $proPreferencesJson
    ) {
      status errorMessage
      projectRequest { id status }
      completenessScore
      completenessMissing
    }
  }
`;

// Guided photo checklist for the chosen category (universal + category slots).
// Mirrors besthand-api requiredPhotoSlots(category, projectRequestId).
const Q_PHOTO_SLOTS = `
  query ($category: String!, $projectRequestId: String) {
    requiredPhotoSlots(category: $category, projectRequestId: $projectRequestId) {
      status errorMessage
      photoSlots { key label required filled }
    }
  }
`;

// Record an already-uploaded photo against a slot on the draft request.
const M_ATTACH_MEDIA = `
  mutation (
    $projectRequestId: String!, $kind: String!, $slot: String,
    $storageKey: String!, $cdnUrl: String
  ) {
    attachProjectMedia(
      projectRequestId: $projectRequestId, kind: $kind, slot: $slot,
      storageKey: $storageKey, cdnUrl: $cdnUrl
    ) {
      status errorMessage
      completenessScore
      completenessMissing
    }
  }
`;

// --- Photo handoff (scan-to-add-photos-from-your-phone) ---
// Decoupled bucket on besthand-api keyed by a random sessionId (the id is the
// capability). The PC opens a session, encodes <origin>/m/photos/<id> into a QR;
// the phone scans, captures, and appends; the PC polls and merges.
const M_CREATE_HANDOFF = `
  mutation {
    createPhotoHandoffSession {
      status errorMessage sessionId
    }
  }
`;
const Q_HANDOFF = `
  query ($sessionId: String!) {
    photoHandoffSession(sessionId: $sessionId) {
      status errorMessage
      photos { url slot name }
    }
  }
`;

// Final publication gate — enforces completeness >= 70 and (if enabled) the visit fee.
const M_PUBLISH = `
  mutation ($projectRequestId: String!) {
    publishProjectRequest(projectRequestId: $projectRequestId) {
      status errorMessage
      projectRequest { id status }
      completenessScore
      completenessMissing
    }
  }
`;

// AI "describe your project in your own words" assistant. The homeowner types a
// plain-language description; besthand-api's aiProjectAssistant (Claude) maps it
// to ONE project category, drafts a clean description, and best-guesses the
// universal project fields. DRAFT ONLY — the wizard pre-fills its own state and
// the client reviews/edits every field before the normal publish flow. Reached
// through the SAME /lp-graphql proxy as every other LP call (lpApi).
const M_AI_ASSISTANT = `
  mutation ($message: String!, $history: String) {
    aiProjectAssistant(message: $message, history: $history) {
      status errorMessage
      category projectDescription confidence
      fields followUpQuestion summary
    }
  }
`;

// Read-only: the current project visit fee (USD) so we can warn before publishing.
const Q_VISIT_FEE = `
  query {
    projectVisitFee { status errorMessage visitFeeAmount }
  }
`;

// Resume: the signed-in client's own project requests, newest-first. We pick the
// newest DRAFT (status === 'DRAFT') and rehydrate the wizard from it so a
// refresh / Back / return-visit continues where the user left off instead of
// starting over. Reuses the SAME read besthand-api already exposes for
// /my-projects (additive field selection only — no new resolver).
const Q_MY_DRAFTS = `
  query {
    myProjectRequests {
      status errorMessage
      projectRequests {
        id status category subcategory title description
        propertyType ambitionLevel
        siteAddressLine1 siteCity siteState siteZip
        budgetMin budgetMax
        measurementsJson timelineJson categorySpecificDataJson intakeAnswersJson
        createdAt
      }
    }
  }
`;

// ---- Reference data ---------------------------------------------------------

// LP-NATIVE category picker (large-projects ONLY). createProjectRequest.category
// is a fixed 7-value ENUM on besthand-api (ALLOWED_CATEGORIES in
// createProjectRequest.js, and the same closed list aiProjectAssistant grounds
// on). The wizard's step-1 tiles ARE these enum values — the user's pick maps 1:1
// to what createProjectRequest receives. This catalog is fully LP-owned: it does
// NOT come from the small-jobs Section/Category/SubCategory tables, so a small job
// can never leak into a large-project request.
//
// Each entry: { value (the API enum), label, icon (inline SVG path), subs (a small
// STATIC list of LP sub-options for the chosen category — also LP-owned, never
// pulled from small-jobs SubCategory rows) }.
const CATEGORY_ENUMS = ['KITCHEN', 'BATHROOM', 'BASEMENT', 'EXTERIOR', 'ROOFING', 'INSTALLATION', 'OTHER'];
const LP_CATEGORIES = [
  {
    value: 'KITCHEN',
    label: 'Kitchen remodel',
    // stove / range icon
    icon: 'M4 4h16v16H4zM4 9h16M8 4v5M7 13.5h.01M7 16.5h.01M12 13.5h.01M12 16.5h.01',
    subs: ['Full remodel', 'Cabinets', 'Countertops', 'Flooring', 'Island add', 'Appliances'],
  },
  {
    value: 'BATHROOM',
    label: 'Bathroom remodel',
    // shower/tub icon
    icon: 'M4 12h16v3a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4v-3ZM6 12V7a2 2 0 0 1 2-2h1M9 5v2',
    subs: ['Full remodel', 'Walk-in shower', 'Tub-to-shower', 'Vanity & sink', 'Tile & flooring', 'Plumbing fixtures'],
  },
  {
    value: 'BASEMENT',
    label: 'Basement finishing',
    // layered floors icon
    icon: 'M3 7l9-4 9 4-9 4-9-4ZM3 12l9 4 9-4M3 17l9 4 9-4',
    subs: ['Full finish', 'Egress & framing', 'Bathroom add', 'Waterproofing', 'Flooring', 'Entertainment room'],
  },
  {
    value: 'EXTERIOR',
    label: 'Exterior & outdoor',
    // house + sun icon
    icon: 'M3 12l9-8 9 8M5 10v10h14V10M9 20v-6h6v6',
    subs: ['Siding', 'Deck or patio', 'Fencing', 'Windows & doors', 'Landscaping', 'Driveway'],
  },
  {
    value: 'ROOFING',
    label: 'Roofing',
    // roof icon
    icon: 'M2 12 12 4l10 8M6 11v8h12v-8M9 19v-4h6v4',
    subs: ['Full replacement', 'Repair', 'Gutters', 'Skylights', 'Flat roof', 'Inspection'],
  },
  {
    value: 'INSTALLATION',
    label: 'Major installation',
    // tool/wrench icon (reuses I_TOOL look)
    icon: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2-2 2.6-2.6Z',
    subs: ['HVAC system', 'Whole-home flooring', 'Cabinetry', 'Appliances', 'Fixtures', 'Built-ins'],
  },
  {
    value: 'OTHER',
    label: 'Something else',
    // generic spark/star icon
    icon: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2 2M16 16l2 2M18 6l-2 2M8 16l-2 2',
    subs: ['Whole-home renovation', 'Addition', 'Garage / outbuilding', 'Multi-room project', 'Not sure yet'],
  },
];

// Lookup the LP category entry by its enum value.
function lpCategoryByValue(value) {
  return LP_CATEGORIES.find((c) => c.value === value) || null;
}

// Client-side mirror of besthand-api computeCompletenessScore so the wizard can
// show a live progress bar + "what's left" BEFORE the draft is created on the
// server (logged-out clients fill everything first). The server remains the
// source of truth: publishProjectRequest recomputes and gates at 70. Weights
// here match the API helper exactly (universal 25 / measurements 10 / photos 30 /
// video 5 / conditional 20 / existing 5 / preferences 5).
function estimateCompleteness(args) {
  const {
    selection, detailsCity, detailsState, detailsZip, detailsAddress,
    budgetKey, timeline, measures, requiredSlots, slotPhotos, totalPhotoCount,
    propertyType, ambitionLevel,
  } = args;

  const missing = [];

  // Universal (25): mirrors the server's 9 UNIVERSAL_FIELDS — title, description,
  // category, the 4 site-address parts, propertyType, ambitionLevel. The wizard
  // captures all of them, so a complete project earns the full weight.
  const universalChecks = [
    !!(selection && selection.subCategoryName), // title
    true, // description (we always send a generated title-based description fallback)
    !!(selection && selection.categoryName), // category
    !!(detailsAddress && detailsAddress.trim()), // siteAddressLine1
    !!(detailsCity && detailsCity.trim()), // siteCity
    !!(detailsState && /^[A-Z]{2}$/.test(detailsState)), // siteState
    !!(detailsZip && /^\d{5}$/.test(String(detailsZip).trim())), // siteZip
    !!propertyType, // propertyType
    !!ambitionLevel, // ambitionLevel
  ];
  const universalFilled = universalChecks.filter(Boolean).length;
  const universalScore = Math.round((universalFilled / universalChecks.length) * 25);
  if (!(detailsAddress && detailsAddress.trim())) missing.push('A street address');
  if (!propertyType) missing.push('Property type');

  // Measurements + budget/timeline (10).
  const areaOk = measures && String(measures.areaSqFt || '').trim() !== '';
  const ceilOk = measures && String(measures.ceilingHeightFt || '').trim() !== '';
  const budgetOk = !!budgetKey;
  const timelineOk = !!timeline;
  const measureUnits = [areaOk, ceilOk, budgetOk || timelineOk];
  const measureScore = Math.round((measureUnits.filter(Boolean).length / 3) * 10);
  if (!areaOk || !ceilOk) missing.push('Area (sq ft) and ceiling height');

  // Photos (30): half for count >= 8, half for required slots covered.
  const reqSlots = (requiredSlots || []).filter((sl) => sl.required);
  const coveredReq = reqSlots.filter((sl) => (slotPhotos && slotPhotos[sl.key])).length;
  const countComp = totalPhotoCount >= TARGET_PHOTO_COUNT ? 1 : totalPhotoCount / TARGET_PHOTO_COUNT;
  const slotComp = reqSlots.length > 0 ? coveredReq / reqSlots.length : 0;
  const photoScore = Math.round(((countComp + slotComp) / 2) * 30);
  if (totalPhotoCount < TARGET_PHOTO_COUNT) {
    missing.push(`${TARGET_PHOTO_COUNT - totalPhotoCount} more photo(s) (target ${TARGET_PHOTO_COUNT})`);
  }
  if (reqSlots.length > 0 && coveredReq < reqSlots.length) {
    missing.push(`${reqSlots.length - coveredReq} required guided photo(s)`);
  }

  // Video 360 (5) — optional, captured via a guided "video_360" slot if present.
  const hasVideo = !!(slotPhotos && slotPhotos.video_360);
  const videoScore = hasVideo ? 5 : 0;

  // Conditional (20): the server scores ProjectRequest.categorySpecificData
  // against the per-category field list. For the OTHER bucket that list is empty,
  // so the full weight is awarded; for richer categories the wizard's intake
  // answers only partially overlap, so the real score may be lower. We award the
  // full weight optimistically here; the server recomputes and gates at publish.
  const conditionalScore = 20;

  // Existing conditions (5) + pro preferences (5): the wizard always submits a
  // small JSON block for each at publish time (built from timeline + budget),
  // so both earn their weight.
  const existingScore = 5;
  const prefsScore = 5;

  const score = Math.min(
    100,
    universalScore + measureScore + photoScore + videoScore +
      conditionalScore + existingScore + prefsScore,
  );
  return { score, missing };
}

// Budget buckets (USD). value = [min, max|null].
const BUDGET_BUCKETS = [
  ['Under $500', 0, 500],
  ['$500 – $1,500', 500, 1500],
  ['$1,500 – $5,000', 1500, 5000],
  ['$5,000 – $15,000', 5000, 15000],
  ['$15,000 – $50,000', 15000, 50000],
  ['$50,000+', 50000, null],
  ['Not sure yet', null, null],
];

// "How did you hear about us?" — optional marketing attribution captured in the
// wizard and persisted on the User (acquisitionSource) at the submit-time createUser.
const ACQUISITION_OPTIONS = [
  ['GOOGLE', 'Google / search engine'],
  ['REFERRAL', 'Friend or referral'],
  ['SOCIAL', 'Social media'],
  ['AD', 'Online ad'],
  ['BLOG', 'News / blog'],
  ['OTHER', 'Other'],
];

const TIMELINE_BUCKETS = [
  'Urgent — within 48 hours',
  'Within a week',
  'Within 2–4 weeks',
  'In 1–3 months',
  'Flexible / just planning',
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

const STEPS = [
  { key: 'service', label: 'Service' },
  { key: 'intake', label: 'Details' },
  { key: 'photos', label: 'Photos' },
  { key: 'measure', label: 'Measurements' },
  { key: 'project', label: 'Budget & timeline' },
  { key: 'review', label: 'Review' },
];

// Step indices (kept as named constants so the flow is readable). Success is a
// pseudo-step shown after publishing.
const STEP_SERVICE = 0;
const STEP_INTAKE = 1;
const STEP_PHOTOS = 2;
const STEP_MEASURE = 3;
const STEP_PROJECT = 4;
const STEP_REVIEW = 5;
const STEP_SUCCESS = 6;

// Completeness gate that besthand-api enforces in publishProjectRequest.
const MIN_COMPLETENESS_SCORE = 70;

// ---- Resume / history plumbing (W3a + W3b) ---------------------------------
// sessionStorage key for the lightweight wizard autosave. This is the
// belt-and-suspenders that covers a PRE-DRAFT (no concrete service chosen yet)
// and logged-OUT guests, who cannot persist a server DRAFT until they auth.
const LP_AUTOSAVE_KEY = 'bh_lp_autosave_v1';
// Debounce for the best-effort server-side DRAFT autosave on each next().
const LP_DRAFT_SAVE_DEBOUNCE_MS = 600;

// Spec §6.5: a contractor should be able to quote without a site visit, so we
// aim for at least this many guided photos.
const TARGET_PHOTO_COUNT = 8;

// Category-relevant measurements captured in step 3 → ProjectRequest.measurements
// (JSON). areaSqFt + ceilingHeightFt are the two keys the server scores
// (see computeCompletenessScore.MEASUREMENT_REQUIRED_KEYS); the rest add detail.
const MEASURE_FIELDS = [
  { key: 'areaSqFt', label: 'Work area (sq ft)', placeholder: 'e.g. 180', required: true, hint: 'Approximate floor area of the work zone.' },
  { key: 'ceilingHeightFt', label: 'Ceiling height (ft)', placeholder: 'e.g. 8', required: true, hint: 'Floor-to-ceiling height.' },
  { key: 'wallLengthFt', label: 'Longest wall (ft)', placeholder: 'e.g. 14', required: false },
  { key: 'doorways', label: 'Number of doorways', placeholder: 'e.g. 2', required: false },
  { key: 'windows', label: 'Number of windows', placeholder: 'e.g. 3', required: false },
];

// LP-NATIVE intake (replaces the small-jobs categoryIntakeQuestions / CategoryField
// step). These are the universal large-project fields — the same ones
// aiProjectAssistant returns — so the wizard never reads any small-jobs
// Category/SubCategory/CategoryField row. The per-category sub-option list comes
// from the chosen LP_CATEGORIES entry's `subs` (a static LP map). A short scope
// note rounds out the step. All of this flows into the existing project fields the
// API already accepts (subcategory, description, categorySpecificData).
const LP_SCOPE_PLACEHOLDER =
  'Describe what you want done — rooms involved, finishes you have in mind, anything a company should know to quote accurately.';

// Property type (maps to ProjectRequest.propertyType ENUM) + ambition level
// (ProjectRequest.ambitionLevel ENUM). Both feed the server "universal" score.
const PROPERTY_TYPES = [
  ['Single-family home', 'SINGLE_FAMILY'],
  ['Townhouse', 'TOWNHOUSE'],
  ['Condo', 'CONDO'],
  ['Mobile home', 'MOBILE_HOME'],
  ['Commercial', 'COMMERCIAL'],
];
const AMBITION_LEVELS = [
  ['Budget-friendly', 'ECONOMIC'],
  ['Standard', 'STANDARD'],
  ['Premium', 'PREMIUM'],
  ['Luxury', 'LUXURY'],
];

// Show the first N multi-select chips, then a "More" affordance to reveal the rest.
const MULTI_VISIBLE = 6;

const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];

// ----------------------------------------------------------------------------

// Single inline-SVG icon component — no external icon import (none invented).
const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const I_TOOL = 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2-2 2.6-2.6Z';
const I_UPLOAD = 'M12 16V4m0 0 4 4m-4-4-4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2';
const I_CHECK = 'M20 6 9 17l-5-5';
const I_CHEVRON = 'M9 6l6 6-6 6';
// Extra inline icon paths for the landing (each is a literal <path d>, none invented).
const I_SHIELD = 'M12 2 4 6v6c0 5 3.5 8 8 10 4.5-2 8-5 8-10V6l-8-4Z';
const I_QUOTE = 'M3 7h18M3 12h18M3 17h12';
const I_ESCROW = 'M12 1v22M5 5h11a3 3 0 0 1 0 6H8a3 3 0 0 0 0 6h11';
const I_MASK = 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z';

// ---- Landing content (premium Large Projects value-prop, mirrors CompanyAuth) ----

// Benefit cards under the hero.
const VALUE_PROPS = [
  {
    d: I_SHIELD,
    title: 'Verified, escrow-protected companies',
    body: 'Every company is license- and insurance-verified, and your payments are held in escrow and released on milestones — so your money is protected from start to finish.',
  },
  {
    d: I_MASK,
    title: 'Identities masked until you choose',
    body: 'Describe your project once and receive competitive quotes. Companies stay anonymous until you decide who to hire — no cold calls, no pressure.',
  },
  {
    d: I_QUOTE,
    title: 'One project, multiple competitive quotes',
    body: 'Big renovations and multi-trade jobs get matched to qualified companies who quote against each other, so you get a fair price on the work.',
  },
];

// Trust signals row under the hero CTAs.
const TRUST_SIGNALS = [
  { d: I_SHIELD, label: 'Verified & insured companies' },
  { d: I_ESCROW, label: 'Escrow-protected payments' },
  { d: I_CHECK, label: 'No lead fees' },
  { d: I_MASK, label: 'Identities masked until you choose' },
];

// "How it works" — the homeowner's path for a large project.
const STEPS_HOW = [
  { n: '1', title: 'Describe your project', body: 'Tell us about your renovation or multi-trade job — a few quick questions, no site visit needed.' },
  { n: '2', title: 'Get competitive quotes', body: 'Verified companies compete for your project. Their identities stay masked until you choose.' },
  { n: '3', title: 'Compare & hire', body: 'Review quotes side by side, ask questions, and hire the company that fits your job and budget.' },
  { n: '4', title: 'Pay safely with escrow', body: 'Funds are held in escrow and released on milestones as the work is completed to your satisfaction.' },
];

class PostAProject extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Landing gate: the page opens on a premium Large Projects landing (mirrors
      // CompanyAuth). The primary CTA flips `started` to true, which swaps the
      // landing for the existing wizard (step 0). Nothing else about the wizard,
      // its state, or the submit-time auth changes.
      started: false,
      loading: true,
      error: '',
      step: 0,

      // ---- auth-at-submit (logged-out clients fill everything; gate only here) ----
      // authOpen flips the review step into the inline phone+OTP auth panel when an
      // unauthenticated user tries to submit. Answers above are untouched, so when
      // auth succeeds we run the real submit with the data already in state.
      authOpen: false,
      authPhase: 'phone', // 'phone' -> 'otp'
      authSaving: false,
      authError: '',
      authNotice: '',
      authFirstName: '',
      authLastName: '',
      authEmail: '',
      phoneDialCode: '+1',
      phoneNumber: '',
      otp: '',

      // AI "describe your project in your own words" assistant (start of the wizard)
      aiText: '', // homeowner's free-text description
      aiLoading: false,
      aiError: '', // soft — never a dead-end; user can still fill the wizard manually
      aiFollowUp: '', // ONE short question from the model (inline, one turn)
      aiCategory: '', // suggested category enum (hint only — user still picks a concrete service)
      aiApplied: false, // collapses the panel once a draft has been pre-filled

      // LP-NATIVE category selection. `selection` is set from the LP category
      // tiles (LP_CATEGORIES) — categoryValue IS the createProjectRequest enum.
      // It does NOT come from the small-jobs Section/Category/SubCategory catalog.
      // selection: { categoryValue, sectionName, categoryId(null), categoryName,
      //              subCategoryId(null), subCategoryName }
      selection: null,

      // intake — LP-NATIVE static fields (no small-jobs categoryIntakeQuestions).
      // `questions`/`answers` remain as empty defaults so the few shared helpers
      // (buildIntakeAnswers/intakeZip) stay no-ops; the LP intake step writes the
      // scope into `description` and the sub-option into selection.subCategoryName.
      questionsLoading: false,
      questions: [],
      answers: {}, // questionId -> value (string | string[])
      multiExpanded: {}, // questionId -> bool ("More" revealed)
      uploads: {}, // questionId -> { uploading, error }
      photos: {}, // questionId -> [{ url, name }]

      // guided photos (anti-circumvention checklist)
      slotsLoading: false,
      photoSlots: [], // [{ key, label, required, filled }]
      slotUploads: {}, // slotKey -> { uploading, error }
      slotPhotos: {}, // slotKey -> { url, name }

      // photo handoff (scan-to-add-photos-from-your-phone)
      handoffOpen: false,
      handoffLoading: false,
      handoffError: '',
      handoffSessionId: '',
      handoffUrl: '', // <origin>/m/photos/<sessionId>
      handoffQrSvg: '', // inline SVG data-uri for the QR
      handoffCount: 0, // how many phone photos have landed

      // measurements (-> ProjectRequest.measurements JSON)
      measures: {}, // { areaSqFt, ceilingHeightFt, wallLengthFt, doorways, windows }
      propertyType: '', // ENUM (SINGLE_FAMILY, ...)
      ambitionLevel: 'STANDARD', // ENUM — sensible default

      // visit fee surfaced before publishing
      visitFeeAmount: null,

      // project details
      budgetKey: '',
      timeline: '',
      detailsZip: '',
      detailsCity: '',
      detailsState: '',
      detailsAddress: '',
      // Google Places autocomplete for the street-address field. Inert (empty dropdown) when no
      // server key is configured — the field then behaves as a plain manual text input.
      addrSuggestions: [],
      addrSuggestOpen: false,
      addrSuggestLoading: false,
      description: '',
      acquisitionSource: '', // optional "How did you hear about us?" (sent to createUser at submit)

      // ---- progressive DRAFT + resume (W3b) ----
      // The server-side DRAFT id, created as soon as a concrete service is
      // chosen (logged-in clients) and reused for every subsequent best-effort
      // updateProjectRequest autosave and for the final publish path. null until
      // a service is chosen / the user is signed in.
      draftId: null,
      // A newest in-progress DRAFT found on mount is offered as "Continue your
      // project" rather than silently overwriting the current (possibly fresh)
      // session. `resumeOffer` holds the rehydrated state we would apply.
      resumeOffer: null,

      // submit
      submitting: false,
      created: null, // projectRequest after success
      publishError: '', // server-side publish block (score < 70 / no card / state closed)
      publishScore: null,
      publishMissing: [],
    };
    this.fileInputs = {};
  }

  componentDidMount() {
    // LP-NATIVE: the step-1 category tiles are a static local catalog
    // (LP_CATEGORIES) — there is no catalog fetch, so the wizard is ready
    // immediately (no small-jobs customProjectSections read).
    this.setState({ loading: false });

    // ---- W3a: hardware/gesture/browser Back maps to in-wizard back ----------
    // We drive the browser history ourselves with raw History API entries
    // (?step=N + a { lpStep } marker) so each wizard step is its own history
    // entry. The popstate listener turns a browser Back into an in-wizard
    // back() (restoring state.step) instead of letting the route unmount; only
    // Back from step 0 leaves the wizard. SSR-safe: window is only touched here
    // and in handlers, never at construction/render.
    if (typeof window !== 'undefined') {
      this.onPopState = (e) => this.handlePopState(e);
      window.addEventListener('popstate', this.onPopState);
    }

    // ---- W3b: rehydrate from a sessionStorage autosave (guests / pre-draft) --
    // This restores the lightweight in-progress state on a refresh even before
    // any server DRAFT exists. The server DRAFT (below) takes precedence when it
    // is newer and more complete.
    this.restoreAutosave();

    // ---- W3b: offer "Continue your project" from the newest server DRAFT -----
    // Only for signed-in clients (a logged-out user has no server DRAFT yet).
    if (this.isAuthed()) {
      this.loadResumeDraft();
    }
  }

  componentWillUnmount() {
    this.stopHandoffPolling();
    if (typeof window !== 'undefined' && this.onPopState) {
      window.removeEventListener('popstate', this.onPopState);
    }
    if (this.draftSaveTimer) {
      clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = null;
    }
    if (this._addrTimer) {
      clearTimeout(this._addrTimer);
      this._addrTimer = null;
    }
  }

  // ==========================================================================
  // W3a — browser history per wizard step (Back = in-wizard back, never home)
  // ==========================================================================

  // Push a NEW history entry for the given step (used when advancing forward,
  // and once when entering the wizard). Each entry carries a { lpStep } marker
  // and a ?step=N query so a refresh/Back lands on the right step. Raw History
  // API only — we deliberately bypass the shared `history` package router here
  // so a same-path search change reconciles the existing component instance
  // (state intact) rather than re-resolving and remounting it.
  // eslint-disable-next-line class-methods-use-this
  pushStepHistory(step, replace) {
    if (typeof window === 'undefined' || !window.history) return;
    try {
      const url = `${window.location.pathname}?step=${step}`;
      const stateObj = { lpStep: step };
      if (replace) window.history.replaceState(stateObj, '', url);
      else window.history.pushState(stateObj, '', url);
    } catch (err) { /* ignore — history is a progressive enhancement */ }
  }

  // popstate handler: when the wizard is active, map a browser/gesture/hardware
  // Back to the in-wizard back() (set state.step from the popped marker). Only
  // Back from step 0 falls through and leaves the wizard (we do nothing, so the
  // route navigates away normally).
  handlePopState(e) {
    if (!this.state.started) return; // not in the wizard — let the route handle it
    if (this.state.step === STEP_SUCCESS) return; // post-publish — let Back leave
    const marker = e && e.state && typeof e.state.lpStep === 'number' ? e.state.lpStep : null;

    // If the popped entry is a wizard step, jump straight to it (Back or Forward).
    if (marker != null && marker >= STEP_SERVICE && marker <= STEP_REVIEW) {
      this.applyHistoryStep(marker);
      return;
    }
    // No marker (we popped past the wizard's first entry) -> step back one;
    // from step 0 there's nothing left, so the route is allowed to unmount.
    if (this.state.step > STEP_SERVICE) {
      this.applyHistoryStep(this.state.step - 1);
    }
  }

  // Set the wizard to a specific step in response to a history pop WITHOUT
  // pushing a new entry (the pop already moved the history cursor). Mirrors the
  // per-step teardown back() does (stop the phone handoff when leaving photos).
  applyHistoryStep(step) {
    const leavingPhotos = this.state.step === STEP_PHOTOS && step !== STEP_PHOTOS;
    if (leavingPhotos) this.stopHandoffPolling();
    this.setState((st) => ({
      step,
      error: '',
      handoffOpen: leavingPhotos ? false : st.handoffOpen,
    }));
  }

  // ==========================================================================
  // W3b — sessionStorage autosave (guests + pre-DRAFT) + resume rehydrate
  // ==========================================================================

  // Lightweight snapshot of the in-progress wizard that survives a refresh even
  // before any server DRAFT exists. Only the JSON-friendly intake state; large
  // blobs (uploaded photo lists, the section catalog) are not mirrored.
  autosaveSnapshot() {
    const st = this.state;
    return {
      v: 1,
      started: st.started,
      step: st.step,
      selection: st.selection,
      answers: st.answers,
      measures: st.measures,
      propertyType: st.propertyType,
      ambitionLevel: st.ambitionLevel,
      budgetKey: st.budgetKey,
      timeline: st.timeline,
      detailsZip: st.detailsZip,
      detailsCity: st.detailsCity,
      detailsState: st.detailsState,
      detailsAddress: st.detailsAddress,
      description: st.description,
      draftId: st.draftId,
    };
  }

  writeAutosave() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      window.sessionStorage.setItem(LP_AUTOSAVE_KEY, JSON.stringify(this.autosaveSnapshot()));
    } catch (err) { /* quota / private mode — non-fatal */ }
  }

  clearAutosave() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try { window.sessionStorage.removeItem(LP_AUTOSAVE_KEY); } catch (err) { /* ignore */ }
  }

  // Restore the sessionStorage autosave on mount (refresh resilience for guests
  // and the pre-DRAFT case). A server DRAFT, when present, is offered separately
  // and the user chooses to continue it.
  restoreAutosave() {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    let snap = null;
    try {
      const raw = window.sessionStorage.getItem(LP_AUTOSAVE_KEY);
      if (raw) snap = JSON.parse(raw);
    } catch (err) { snap = null; }
    if (!snap || snap.v !== 1 || !snap.started) return;

    const safeStep = Number.isFinite(snap.step)
      ? Math.max(STEP_SERVICE, Math.min(STEP_REVIEW, snap.step))
      : STEP_SERVICE;

    this.setState(
      {
        started: true,
        step: safeStep,
        selection: snap.selection || null,
        answers: snap.answers || {},
        measures: snap.measures || {},
        propertyType: snap.propertyType || '',
        ambitionLevel: snap.ambitionLevel || 'STANDARD',
        budgetKey: snap.budgetKey || '',
        timeline: snap.timeline || '',
        detailsZip: snap.detailsZip || '',
        detailsCity: snap.detailsCity || '',
        detailsState: snap.detailsState || '',
        detailsAddress: snap.detailsAddress || '',
        description: snap.description || '',
        draftId: snap.draftId || null,
      },
      () => {
        // LP-NATIVE: intake is static (no per-category question fetch). Re-seed
        // only the guided photo slots, which are keyed off the LP category enum.
        if (snap.selection && snap.selection.categoryValue && safeStep >= STEP_PHOTOS) {
          this.loadPhotoSlots();
        }
        // Establish a baseline history entry for the restored step.
        this.pushStepHistory(safeStep, true);
      },
    );
  }

  // Query the newest in-progress DRAFT for the signed-in client and, if one
  // exists, stage it as a "Continue your project" offer (rehydrated state). We
  // do NOT auto-apply it: the user explicitly chooses to resume so a deliberate
  // fresh start is never hijacked.
  async loadResumeDraft() {
    const res = await lpApi(Q_MY_DRAFTS);
    const env = res && res.data && res.data.myProjectRequests;
    if ((res && res.errors) || !env || env.status !== 200) return;
    const list = (env.projectRequests || []).filter(Boolean);
    // newest-first already (createdAt DESC on the API); pick the first DRAFT.
    const draft = list.find((p) => p && p.status === 'DRAFT');
    if (!draft) return;
    const offer = this.rehydrateFromDraft(draft);
    if (offer) this.setState({ resumeOffer: offer });
  }

  // Build a state patch from a server DRAFT row (the inverse of doSubmit's
  // packing). Restores selection, intake answers, measurements, budget/timeline,
  // property/ambition and description. Photos are re-attached server-side, so we
  // do not re-download them; the photo step simply reflects what's on the draft.
  // eslint-disable-next-line class-methods-use-this
  rehydrateFromDraft(draft) {
    if (!draft || !draft.id) return null;
    const patch = {
      draftId: String(draft.id),
      propertyType: draft.propertyType || '',
      ambitionLevel: draft.ambitionLevel || 'STANDARD',
      description: draft.description || '',
      detailsAddress: draft.siteAddressLine1 && draft.siteAddressLine1 !== 'To be confirmed' ? draft.siteAddressLine1 : '',
      detailsCity: draft.siteCity || '',
      detailsState: draft.siteState || '',
      detailsZip: draft.siteZip || '',
    };

    // LP-NATIVE selection: rebuild from the project's enum category + the
    // LP-owned data the wizard packed into categorySpecificData at submit. We key
    // off the LP enum (draft.category / csd.categoryValue), NOT any small-jobs
    // category id.
    let selection = null;
    let budgetLabel = '';
    let csd = null;
    if (draft.categorySpecificDataJson) {
      try { csd = JSON.parse(draft.categorySpecificDataJson); } catch (err) { csd = null; }
    }
    const enumValue = (csd && csd.categoryValue)
      || (draft.category && CATEGORY_ENUMS.includes(String(draft.category).toUpperCase())
        ? String(draft.category).toUpperCase()
        : '');
    const lpCat = enumValue ? lpCategoryByValue(enumValue) : null;
    if (lpCat) {
      selection = {
        categoryValue: lpCat.value,
        sectionName: 'Large project',
        categoryId: null,
        categoryName: (csd && csd.categoryName) || lpCat.label,
        subCategoryId: null,
        subCategoryName: (csd && csd.subCategoryName) || draft.subcategory || lpCat.label,
      };
    }
    if (csd && csd.budgetLabel) budgetLabel = csd.budgetLabel;
    if (selection) patch.selection = selection;

    // budget — prefer the saved label; else map the numeric range to a bucket.
    if (budgetLabel) patch.budgetKey = budgetLabel;
    else if (draft.budgetMin != null || draft.budgetMax != null) {
      const bk = this.budgetKeyFor(draft.budgetMin, draft.budgetMax);
      if (bk) patch.budgetKey = bk;
    }

    // timeline label.
    if (draft.timelineJson) {
      try { const tl = JSON.parse(draft.timelineJson); if (tl && tl.label) patch.timeline = tl.label; } catch (err) { /* ignore */ }
    }

    // measurements.
    if (draft.measurementsJson) {
      try { const m = JSON.parse(draft.measurementsJson); if (m && typeof m === 'object') patch.measures = m; } catch (err) { /* ignore */ }
    }

    // intake answers (questionId -> value).
    if (draft.intakeAnswersJson) {
      try { const a = JSON.parse(draft.intakeAnswersJson); if (a && typeof a === 'object') patch.answers = a; } catch (err) { /* ignore */ }
    }

    return patch;
  }

  // User accepted the "Continue your project" offer: apply the rehydrated state,
  // enter the wizard, re-seed the intake questions, and land on the review step
  // (everything is filled, so they can edit any step or publish).
  acceptResume() {
    const offer = this.state.resumeOffer;
    if (!offer) return;
    this.setState(
      {
        ...offer,
        resumeOffer: null,
        started: true,
        step: STEP_REVIEW,
        error: '',
      },
      () => {
        if (offer.selection && offer.selection.categoryValue) {
          this.loadPhotoSlots();
        }
        this.loadVisitFee();
        this.writeAutosave();
        this.pushStepHistory(STEP_REVIEW, true);
      },
    );
  }

  dismissResume() {
    this.setState({ resumeOffer: null });
  }

  // ==========================================================================
  // W3b — progressive server-side DRAFT (create on service-choice, autosave)
  // ==========================================================================

  // Create the DRAFT as soon as a concrete service is chosen — but ONLY for a
  // signed-in client (a guest cannot create a server DRAFT; auth stays at the
  // end, so guests rely on the sessionStorage autosave until they authenticate).
  // Best-effort: a failure here never blocks the wizard; the final publish path
  // (doSubmit) still creates the DRAFT if one was not made progressively.
  async ensureDraft() {
    if (this.state.draftId) return this.state.draftId;
    if (!this.isAuthed()) return null;
    const { selection } = this.state;
    if (!selection || !selection.categoryValue) return null;

    const zip = this.state.detailsZip || this.intakeZip();
    const category = this.categoryEnum();
    const title = `${selection.subCategoryName} — ${selection.categoryName}`.slice(0, 160);
    // createProjectRequest requires site fields; site is collected later, so we
    // send safe placeholders now and overwrite them at submit (doSubmit passes
    // the real address). Identical contract to the existing submit-time create.
    const createRes = await lpApi(M_CREATE, {
      category,
      siteAddressLine1: (this.state.detailsAddress || '').trim() || 'To be confirmed',
      siteCity: (this.state.detailsCity || '').trim() || 'To be confirmed',
      siteState: this.state.detailsState || 'PA',
      siteZip: String(zip || '00000').trim(),
      title,
    });
    const c = this.envelope(createRes, 'createProjectRequest');
    if (c.err || !c.env || !c.env.projectRequest || !c.env.projectRequest.id) return null;
    const id = String(c.env.projectRequest.id);
    this.setState({ draftId: id }, () => this.writeAutosave());
    return id;
  }

  // Debounced best-effort autosave of the current wizard state onto the server
  // DRAFT. Mirrors the field packing doSubmit uses (subcategory, description,
  // budget, timeline, measurements, intake answers, category data) so the DRAFT
  // is genuinely resumable. No-op for guests / before a DRAFT exists. NEVER
  // touches the publish path or the visit-fee logic.
  scheduleDraftSave() {
    if (typeof window === 'undefined') return;
    if (this.draftSaveTimer) clearTimeout(this.draftSaveTimer);
    this.draftSaveTimer = setTimeout(() => this.saveDraftProgress(), LP_DRAFT_SAVE_DEBOUNCE_MS);
  }

  async saveDraftProgress() {
    const id = this.state.draftId;
    if (!id || !this.isAuthed()) return;
    const { selection } = this.state;
    if (!selection) return;

    const budget = BUDGET_BUCKETS.find((b) => b[0] === this.state.budgetKey);
    const timelineJson = this.state.timeline ? JSON.stringify({ label: this.state.timeline }) : null;
    const categorySpecific = this.buildCategorySpecific();
    // Best-effort; a failure is swallowed so progress saving never blocks the UI.
    await lpApi(M_UPDATE, {
      projectRequestId: id,
      subcategory: selection.subCategoryName,
      description: (this.state.description || '').trim() || null,
      propertyType: this.state.propertyType || null,
      ambitionLevel: this.state.ambitionLevel || null,
      budgetMin: budget && budget[1] != null ? Number(budget[1]) : null,
      budgetMax: budget && budget[2] != null ? Number(budget[2]) : null,
      timelineJson,
      categorySpecificDataJson: categorySpecific,
      intakeAnswersJson: JSON.stringify(this.buildIntakeAnswers()),
      measurementsJson: this.buildMeasurementsJson(),
    });
  }

  // ---- photo handoff (scan-to-add-photos-from-your-phone) ----

  // Open the panel: create a session on besthand-api, build the mobile URL +
  // QR, and start polling. SSR-safe: window + QR generation only run here (a
  // user click handler), never during render/module load.
  async openHandoff() {
    if (this.state.handoffOpen) return;
    this.setState({ handoffOpen: true, handoffLoading: true, handoffError: '' });
    const res = await lpApi(M_CREATE_HANDOFF);
    const env = res && res.data && res.data.createPhotoHandoffSession;
    if ((res && res.errors) || !env || env.status !== 200 || !env.sessionId) {
      this.setState({
        handoffLoading: false,
        handoffError: (env && env.errorMessage) || 'Could not start the phone handoff. Please try again.',
      });
      return;
    }
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
    const url = `${origin}/m/photos/${env.sessionId}`;
    let qr = '';
    try {
      qr = qrSvg(url, { size: 220, margin: 4 });
    } catch (e) {
      qr = '';
    }
    this.setState({
      handoffLoading: false,
      handoffSessionId: env.sessionId,
      handoffUrl: url,
      handoffQrSvg: qr,
    });
    this.startHandoffPolling(env.sessionId);
  }

  closeHandoff() {
    this.stopHandoffPolling();
    this.setState({ handoffOpen: false });
  }

  startHandoffPolling(sessionId) {
    this.stopHandoffPolling();
    // Poll every ~4s; merge any new phone photos into slotPhotos.
    this.handoffTimer = setInterval(() => this.pollHandoff(sessionId), 4000);
  }

  stopHandoffPolling() {
    if (this.handoffTimer) {
      clearInterval(this.handoffTimer);
      this.handoffTimer = null;
    }
  }

  async pollHandoff(sessionId) {
    const res = await lpApi(Q_HANDOFF, { sessionId });
    const env = res && res.data && res.data.photoHandoffSession;
    if (res && res.errors) return; // transient — keep polling
    if (!env) return;
    if (env.status === 404 || env.status === 410) {
      // Session gone/expired — stop and surface a gentle message.
      this.stopHandoffPolling();
      this.setState({ handoffError: 'This phone code expired. Close and reopen to get a new one.' });
      return;
    }
    if (env.status !== 200 || !Array.isArray(env.photos)) return;

    // Merge phone photos into slotPhotos under synthetic keys, deduped by url,
    // so they count toward completeness and attach at publish like PC uploads.
    this.setState((st) => {
      const next = { ...st.slotPhotos };
      const existingUrls = new Set(
        Object.keys(next).map((k) => next[k] && next[k].url).filter(Boolean),
      );
      let added = 0;
      env.photos.forEach((p, idx) => {
        if (!p || !p.url || existingUrls.has(p.url)) return;
        existingUrls.add(p.url);
        next[`phone_${sessionId.slice(0, 6)}_${idx}`] = {
          url: p.url,
          name: p.name || 'Phone photo',
          fileName: p.url, // attachProjectMedia uses fileName||url as storageKey
        };
        added += 1;
      });
      if (added === 0) return null; // no change
      return { slotPhotos: next, handoffCount: st.handoffCount + added };
    });
  }

  // eslint-disable-next-line class-methods-use-this
  firstError(res) {
    if (res && res.errors && res.errors.length) return res.errors[0].message;
    return 'Something went wrong. Please try again.';
  }

  envelope(res, field) {
    if (res && res.errors) return { err: this.firstError(res) };
    const env = res && res.data && res.data[field];
    if (!env) return { err: 'Unexpected response from the server.' };
    if (env.status === 401) {
      return { err: 'AUTH' };
    }
    if (env.status && env.status !== 200) {
      return { err: env.errorMessage || `Request failed (${env.status}).` };
    }
    return { env };
  }

  // ---- AI "describe your project in your own words" assistant ----------------

  // Call besthand-api's aiProjectAssistant (Claude) through the /lp-graphql proxy
  // and pre-fill the wizard from its DRAFT. This NEVER creates/updates/publishes
  // anything — it only seeds component state. On 503 (AI not configured) or any
  // error we leave a soft notice and let the user fill the wizard manually.
  async runAiAssistant() {
    const message = (this.state.aiText || '').trim();
    if (!message) { this.setState({ aiError: 'Tell us a bit about your project first.' }); return; }
    if (this.state.aiLoading) return;
    this.setState({ aiLoading: true, aiError: '', aiFollowUp: '' });

    // Carry one prior follow-up turn so a second pass can refine (mirrors aiJobAssistant history).
    const history = this.state.aiFollowUp ? `Assistant: ${this.state.aiFollowUp}` : '';
    const res = await lpApi(M_AI_ASSISTANT, { message, history });
    const env = res && res.data && res.data.aiProjectAssistant;

    if ((res && res.errors) || !env) {
      this.setState({ aiLoading: false, aiError: 'The assistant is unavailable right now — no problem, just fill in the steps below.' });
      return;
    }
    if (env.status === 503) {
      this.setState({ aiLoading: false, aiError: 'The assistant isn’t available right now — just fill in the steps below.' });
      return;
    }
    if (env.status && env.status !== 200) {
      this.setState({ aiLoading: false, aiError: env.errorMessage || 'Couldn’t read that — try rephrasing, or fill in the steps below.' });
      return;
    }
    this.applyAiResult(env);
  }

  // Pre-fill the wizard's EXISTING state slots from the AI draft, reusing the same
  // setState mechanism the manual steps use. We never invent fields and never touch
  // the publish/visit-fee logic. The user reviews/edits everything afterward.
  applyAiResult(env) {
    let fields = {};
    if (env.fields) {
      try { const p = JSON.parse(env.fields); if (p && typeof p === 'object') fields = p; } catch (e) { fields = {}; }
    }
    const followUp = (env.followUpQuestion || '').trim();

    this.setState((st) => {
      const next = { aiLoading: false, aiError: '', aiFollowUp: followUp };

      // Free-text description -> the wizard's existing `description` slot.
      if (env.projectDescription && env.projectDescription.trim()) {
        next.description = env.projectDescription.trim();
      }
      // Suggested category enum -> AUTO-SELECT the matching LP tile. Now that
      // step 1 IS the 7-value enum, the AI's category maps 1:1 to a tile, so we
      // select it directly (no more "hint only"). If the user disagrees they can
      // pick a different tile. We only seed `selection` if the user hasn't already
      // chosen one this session.
      const aiCat = env.category ? String(env.category).toUpperCase() : '';
      if (CATEGORY_ENUMS.includes(aiCat)) {
        next.aiCategory = aiCat;
        if (!st.selection || !st.selection.categoryValue) {
          const lpCat = lpCategoryByValue(aiCat);
          if (lpCat) {
            next.selection = {
              categoryValue: lpCat.value,
              sectionName: 'Large project',
              categoryId: null,
              categoryName: lpCat.label,
              subCategoryId: null,
              subCategoryName: lpCat.label,
            };
          }
        }
      }
      // Universal fields -> the same state slots the manual steps write.
      if (fields.propertyType && PROPERTY_TYPES.some((p) => p[1] === fields.propertyType)) {
        next.propertyType = fields.propertyType;
      }
      if (fields.ambitionLevel && AMBITION_LEVELS.some((a) => a[1] === fields.ambitionLevel)) {
        next.ambitionLevel = fields.ambitionLevel;
      }
      // Measurements -> measures map (only the keys the wizard collects).
      const measures = { ...st.measures };
      MEASURE_FIELDS.forEach((f) => {
        if (fields[f.key] != null && Number.isFinite(Number(fields[f.key]))) {
          measures[f.key] = String(Number(fields[f.key]));
        }
      });
      next.measures = measures;
      // Budget min/max -> nearest existing budget bucket label.
      const bk = this.budgetKeyFor(fields.budgetMin, fields.budgetMax);
      if (bk) next.budgetKey = bk;
      // Timeline string -> nearest existing timeline bucket.
      const tl = this.timelineFor(fields.timeline);
      if (tl) next.timeline = tl;

      next.aiApplied = true;
      return next;
    });
  }

  // Map an AI budget range (USD) to the closest existing BUDGET_BUCKETS label.
  // eslint-disable-next-line class-methods-use-this
  budgetKeyFor(min, max) {
    const mn = Number(min);
    const mx = Number(max);
    const ref = Number.isFinite(mx) && mx > 0 ? mx : (Number.isFinite(mn) ? mn : NaN);
    if (!Number.isFinite(ref)) return '';
    let best = null;
    BUDGET_BUCKETS.forEach((b) => {
      const [label, bMin, bMax] = b;
      if (bMin == null && bMax == null) return; // skip "Not sure yet"
      const lo = bMin == null ? 0 : bMin;
      const hi = bMax == null ? Infinity : bMax;
      if (ref >= lo && ref <= hi) best = label;
    });
    return best || '';
  }

  // Map an AI free-text timeline to the closest existing TIMELINE_BUCKETS entry.
  // eslint-disable-next-line class-methods-use-this
  timelineFor(text) {
    if (!text) return '';
    const t = String(text).toLowerCase();
    if (/(urgent|asap|48|immediat|today|tomorrow)/.test(t)) return TIMELINE_BUCKETS[0];
    if (/(week)/.test(t) && !/(weeks)/.test(t)) return TIMELINE_BUCKETS[1];
    if (/(2|two|3|three|4|four).*week|weeks/.test(t)) return TIMELINE_BUCKETS[2];
    if (/(month)/.test(t)) return TIMELINE_BUCKETS[3];
    if (/(flexib|plan|no rush|whenever)/.test(t)) return TIMELINE_BUCKETS[4];
    return '';
  }

  // ---- auth detection (read the id_token cookie set by every web login flow) ----
  // eslint-disable-next-line class-methods-use-this
  isAuthed() {
    if (typeof window === 'undefined') return false;
    return !!getCookies({ name: 'id_token' });
  }

  // ---- service step ----

  // LP-NATIVE step-1 pick: choosing a category TILE sets the project enum
  // directly (selection.categoryValue IS the value sent to createProjectRequest —
  // a 1:1 map, no keyword guessing, no OTHER default). subCategoryName defaults to
  // the category label and can be refined by an LP sub-option chip on the intake
  // step. Downstream per-service state is reset whenever the category changes.
  selectCategory(value) {
    const lpCat = lpCategoryByValue(value);
    if (!lpCat) return;
    this.setState(
      {
        selection: {
          categoryValue: lpCat.value,
          sectionName: 'Large project',
          categoryId: null,
          categoryName: lpCat.label,
          subCategoryId: null,
          subCategoryName: lpCat.label,
        },
        // reset downstream state whenever the category changes
        questions: [],
        answers: {},
        multiExpanded: {},
        photos: {},
        uploads: {},
        photoSlots: [],
        slotPhotos: {},
        slotUploads: {},
        step: STEP_INTAKE,
        error: '',
      },
      () => {
        // W3a: advancing to the intake step gets its own history entry.
        this.pushStepHistory(STEP_INTAKE, false);
        // W3b: persist progress — create the server DRAFT now (signed-in) and
        // mirror the lightweight state to sessionStorage (guests + everyone).
        this.ensureDraft();
        this.writeAutosave();
      },
    );
  }

  // LP-NATIVE intake: pick a static sub-option chip for the chosen category.
  // Stored as the project's subcategory (selection.subCategoryName). Toggling off
  // falls back to the category label so a subcategory is always present.
  selectSubOption(name) {
    this.setState((st) => {
      if (!st.selection) return null;
      const lpCat = lpCategoryByValue(st.selection.categoryValue);
      const fallback = (lpCat && lpCat.label) || st.selection.categoryName;
      const next = st.selection.subCategoryName === name ? fallback : name;
      return { selection: { ...st.selection, subCategoryName: next }, error: '' };
    }, () => { this.writeAutosave(); this.scheduleDraftSave(); });
  }

  // Category enum sent to the API (createProjectRequest + photo slots). It is the
  // user's tile pick, 1:1 — no mapping, no OTHER default beyond a defensive guard
  // for the impossible no-selection case.
  categoryEnum() {
    const { selection } = this.state;
    if (selection && CATEGORY_ENUMS.includes(selection.categoryValue)) return selection.categoryValue;
    return 'OTHER';
  }

  // LP-NATIVE categorySpecificData block written to the project (autosave +
  // publish). LP-owned shape: the enum value, human label, the chosen sub-option,
  // and the budget label. NO small-jobs category/subcategory ids.
  buildCategorySpecific() {
    const { selection } = this.state;
    if (!selection) return JSON.stringify({});
    return JSON.stringify({
      categoryValue: selection.categoryValue,
      categoryName: selection.categoryName,
      subCategoryName: selection.subCategoryName,
      budgetLabel: this.state.budgetKey,
    });
  }

  // Guided photo checklist for the chosen category. Public read on besthand-api,
  // so it works for logged-out clients (no projectRequestId yet → filled=false).
  async loadPhotoSlots() {
    if (this.state.photoSlots.length || this.state.slotsLoading) return;
    this.setState({ slotsLoading: true, error: '' });
    const res = await lpApi(Q_PHOTO_SLOTS, { category: this.categoryEnum() });
    const { env, err } = this.envelope(res, 'requiredPhotoSlots');
    if (err && err !== 'AUTH') {
      this.setState({ slotsLoading: false, error: err });
      return;
    }
    this.setState({ slotsLoading: false, photoSlots: (env && env.photoSlots) || [] });
  }

  // Visit fee (read-only) so the review step can warn before publishing. Needs
  // auth on besthand-api, so we only attempt it once signed in.
  async loadVisitFee() {
    if (this.state.visitFeeAmount != null || !this.isAuthed()) return;
    const res = await lpApi(Q_VISIT_FEE);
    const { env } = this.envelope(res, 'projectVisitFee');
    if (env && env.visitFeeAmount != null) {
      this.setState({ visitFeeAmount: Number(env.visitFeeAmount) });
    }
  }

  // ---- answers ----

  setAnswer(qId, value) {
    this.setState((st) => ({ answers: { ...st.answers, [qId]: value }, error: '' }));
  }

  toggleMulti(qId, label) {
    this.setState((st) => {
      const cur = Array.isArray(st.answers[qId]) ? st.answers[qId] : [];
      const next = cur.includes(label) ? cur.filter((x) => x !== label) : cur.concat(label);
      return { answers: { ...st.answers, [qId]: next }, error: '' };
    });
  }

  expandMulti(qId) {
    this.setState((st) => ({ multiExpanded: { ...st.multiExpanded, [qId]: true } }));
  }

  // ---- photo upload (reuses /uploadCompanyDocument) ----

  async uploadPhoto(qId, file) {
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      this.setState((st) => ({ uploads: { ...st.uploads, [qId]: { uploading: false, error: 'Use a JPG, PNG, WEBP, or PDF file.' } } }));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this.setState((st) => ({ uploads: { ...st.uploads, [qId]: { uploading: false, error: 'File is too large (max 15 MB).' } } }));
      return;
    }
    this.setState((st) => ({ uploads: { ...st.uploads, [qId]: { uploading: true, error: '' } } }));
    try {
      const fd = new FormData();
      fd.append('document', file);
      const resp = await fetch('/uploadCompanyDocument', { method: 'POST', body: fd, credentials: 'same-origin' });
      const json = await resp.json();
      if (json && json.status === 200 && json.documentUrl) {
        this.setState((st) => {
          const cur = Array.isArray(st.photos[qId]) ? st.photos[qId] : [];
          return {
            uploads: { ...st.uploads, [qId]: { uploading: false, error: '' } },
            photos: { ...st.photos, [qId]: cur.concat({ url: json.documentUrl, name: file.name }) },
          };
        });
      } else {
        this.setState((st) => ({ uploads: { ...st.uploads, [qId]: { uploading: false, error: (json && json.errorMessage) || 'Upload failed.' } } }));
      }
    } catch (e) {
      this.setState((st) => ({ uploads: { ...st.uploads, [qId]: { uploading: false, error: `Upload failed: ${e.message}` } } }));
    }
  }

  removePhoto(qId, idx) {
    this.setState((st) => {
      const cur = Array.isArray(st.photos[qId]) ? st.photos[qId].slice() : [];
      cur.splice(idx, 1);
      return { photos: { ...st.photos, [qId]: cur } };
    });
  }

  // ---- guided photo slots (anti-circumvention checklist) ----

  // Upload one photo for a specific slot. The file goes to the SAME web upload
  // route as everything else; the returned URL is held in state and attached to
  // the request (with its slot) at publish time via attachProjectMedia.
  async uploadSlotPhoto(slotKey, file) {
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      this.setState((st) => ({ slotUploads: { ...st.slotUploads, [slotKey]: { uploading: false, error: 'Use a JPG, PNG, WEBP, or PDF file.' } } }));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this.setState((st) => ({ slotUploads: { ...st.slotUploads, [slotKey]: { uploading: false, error: 'File is too large (max 15 MB).' } } }));
      return;
    }
    this.setState((st) => ({ slotUploads: { ...st.slotUploads, [slotKey]: { uploading: true, error: '' } } }));
    try {
      const fd = new FormData();
      fd.append('document', file);
      const resp = await fetch('/uploadCompanyDocument', { method: 'POST', body: fd, credentials: 'same-origin' });
      const json = await resp.json();
      if (json && json.status === 200 && json.documentUrl) {
        this.setState((st) => ({
          slotUploads: { ...st.slotUploads, [slotKey]: { uploading: false, error: '' } },
          slotPhotos: { ...st.slotPhotos, [slotKey]: { url: json.documentUrl, name: file.name, fileName: json.fileName } },
        }));
      } else {
        this.setState((st) => ({ slotUploads: { ...st.slotUploads, [slotKey]: { uploading: false, error: (json && json.errorMessage) || 'Upload failed.' } } }));
      }
    } catch (e) {
      this.setState((st) => ({ slotUploads: { ...st.slotUploads, [slotKey]: { uploading: false, error: `Upload failed: ${e.message}` } } }));
    }
  }

  removeSlotPhoto(slotKey) {
    this.setState((st) => {
      const next = { ...st.slotPhotos };
      delete next[slotKey];
      return { slotPhotos: next };
    });
  }

  // Count of guided photos the client has provided (for the >= 8 target).
  guidedPhotoCount() {
    return Object.keys(this.state.slotPhotos || {}).length;
  }

  // ---- measurements ----

  setMeasure(key, value) {
    // Numbers only (allow a single decimal point).
    const clean = String(value).replace(/[^\d.]/g, '');
    this.setState((st) => ({ measures: { ...st.measures, [key]: clean }, error: '' }));
  }

  // Live completeness estimate (mirror of the server helper) for the bar + checklist.
  completeness() {
    return estimateCompleteness({
      selection: this.state.selection,
      detailsCity: this.state.detailsCity,
      detailsState: this.state.detailsState,
      detailsZip: this.state.detailsZip || this.intakeZip(),
      detailsAddress: this.state.detailsAddress,
      budgetKey: this.state.budgetKey,
      timeline: this.state.timeline,
      measures: this.state.measures,
      requiredSlots: this.state.photoSlots,
      slotPhotos: this.state.slotPhotos,
      totalPhotoCount: this.guidedPhotoCount(),
      propertyType: this.state.propertyType,
      ambitionLevel: this.state.ambitionLevel,
    });
  }

  // ---- validation ----

  // Returns an error string for the intake step, or '' when valid.
  validateIntake() {
    const { questions, answers, photos } = this.state;
    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      if (!q.isRequired) continue; // eslint-disable-line no-continue
      const v = answers[q.id];
      if (q.inputType === 'MULTI_SELECT') {
        if (!Array.isArray(v) || v.length === 0) return `Please answer: ${q.question}`;
      } else if (q.inputType === 'PHOTO') {
        if (!Array.isArray(photos[q.id]) || photos[q.id].length === 0) return `Please add a photo for: ${q.question}`;
      } else if (q.inputType === 'ZIP') {
        if (!v || !/^\d{5}$/.test(String(v).trim())) return `Please enter a valid 5-digit ZIP for: ${q.question}`;
      } else if (v == null || String(v).trim() === '') {
        return `Please answer: ${q.question}`;
      }
    }
    return '';
  }

  // Pull a ZIP captured in any intake ZIP question (so step 3 can pre-fill / dedupe).
  intakeZip() {
    const { questions, answers } = this.state;
    const zipQ = questions.find((q) => q.inputType === 'ZIP');
    if (zipQ && answers[zipQ.id] && /^\d{5}$/.test(String(answers[zipQ.id]).trim())) {
      return String(answers[zipQ.id]).trim();
    }
    return '';
  }

  // Guided-photos step: require every REQUIRED slot to be filled (the core
  // anti-circumvention rule — a company must be able to quote without visiting).
  validatePhotos() {
    const { photoSlots, slotPhotos } = this.state;
    const count = this.guidedPhotoCount();
    // Count-based gate: enough photos = a company can quote without a visit.
    // Phone-handoff captures (the "add photos from your phone" flow) land under
    // synthetic keys and can't target the named guided slots, so a hard
    // "every required slot filled" rule made that flow a dead end. Once the
    // client has at least TARGET_PHOTO_COUNT photos (from slots AND/OR phone),
    // let them advance; the named slots remain on-screen as guidance.
    if (count >= TARGET_PHOTO_COUNT) return '';
    const missingReq = (photoSlots || [])
      .filter((sl) => sl.required && !slotPhotos[sl.key]);
    if (missingReq.length > 0) {
      return `Add at least ${TARGET_PHOTO_COUNT} photos so a company can quote without a site visit (${count}/${TARGET_PHOTO_COUNT}) — including the suggested shots: ${missingReq.map((s) => s.label).join('; ')}.`;
    }
    return `Add at least ${TARGET_PHOTO_COUNT} photos so a company can quote without a site visit (${count}/${TARGET_PHOTO_COUNT}).`;
  }

  validateMeasure() {
    const { measures, propertyType } = this.state;
    if (!measures.areaSqFt || String(measures.areaSqFt).trim() === '') return 'Please enter the work area in square feet.';
    if (!measures.ceilingHeightFt || String(measures.ceilingHeightFt).trim() === '') return 'Please enter the ceiling height in feet.';
    if (!propertyType) return 'Please choose the property type.';
    return '';
  }

  validateProject() {
    const zip = this.state.detailsZip || this.intakeZip();
    // Street address is REQUIRED — a company must know exactly where the work is.
    if (!this.state.detailsAddress || !this.state.detailsAddress.trim()) return 'Please enter the street address where the work will be done.';
    if (!zip || !/^\d{5}$/.test(String(zip).trim())) return 'Please enter a valid 5-digit ZIP code.';
    if (!this.state.detailsState || !/^[A-Z]{2}$/.test(this.state.detailsState)) return 'Please select your state.';
    if (!this.state.detailsCity || !this.state.detailsCity.trim()) return 'Please enter your city.';
    if (!this.state.budgetKey) return 'Please choose a budget range.';
    if (!this.state.timeline) return 'Please choose a timeline.';
    return '';
  }

  // ---- step navigation ----

  // Forward to `nextStep`, optionally merging extra state. W3a: pushes a NEW
  // history entry for the step. W3b: mirrors to sessionStorage and schedules a
  // best-effort server DRAFT autosave so progress is saved as the user goes.
  // `after` runs after the state commit (e.g. loadPhotoSlots / loadVisitFee).
  advanceTo(nextStep, extra, after) {
    this.setState(
      (st) => ({ ...(extra || {}), step: nextStep, error: '' }),
      () => {
        this.pushStepHistory(nextStep, false);
        this.writeAutosave();
        this.scheduleDraftSave();
        if (after) after();
      },
    );
  }

  next() {
    const { step } = this.state;
    if (step === STEP_SERVICE) {
      if (!this.state.selection) { this.setState({ error: 'Please pick a service to continue.' }); return; }
      this.advanceTo(STEP_INTAKE);
      return;
    }
    if (step === STEP_INTAKE) {
      const err = this.validateIntake();
      if (err) { this.setState({ error: err }); return; }
      // Pre-fill the details ZIP from intake, then load the guided photo slots.
      const zip = this.intakeZip();
      this.advanceTo(
        STEP_PHOTOS,
        { detailsZip: this.state.detailsZip || zip },
        () => this.loadPhotoSlots(),
      );
      return;
    }
    if (step === STEP_PHOTOS) {
      const err = this.validatePhotos();
      if (err) { this.setState({ error: err }); return; }
      // Leaving the photos step: stop the phone-handoff poll + close its panel.
      this.stopHandoffPolling();
      this.advanceTo(STEP_MEASURE, { handoffOpen: false });
      return;
    }
    if (step === STEP_MEASURE) {
      const err = this.validateMeasure();
      if (err) { this.setState({ error: err }); return; }
      this.advanceTo(STEP_PROJECT);
      return;
    }
    if (step === STEP_PROJECT) {
      const err = this.validateProject();
      if (err) { this.setState({ error: err }); return; }
      this.advanceTo(STEP_REVIEW, null, () => this.loadVisitFee());
    }
  }

  // Client-router navigation (no full page reload). Replaces the old
  // window.location.href on the success screen so we keep the SPA + Redux store.
  // history is `false` under SSR (process.env.BROWSER guard in src/history.js).
  // eslint-disable-next-line class-methods-use-this
  goTo(path) {
    if (history && typeof history.push === 'function') {
      history.push(path);
    } else if (typeof window !== 'undefined') {
      window.location.href = path; // SSR/no-history fallback
    }
  }

  // Enter the wizard from the landing CTA. W3a: establishes the wizard's first
  // history entry (step 0) so a subsequent Back from step 0 — and only from
  // step 0 — leaves the wizard. W3b: starts the sessionStorage autosave.
  startWizard() {
    this.setState({ started: true, step: STEP_SERVICE, error: '' }, () => {
      this.pushStepHistory(STEP_SERVICE, true);
      this.writeAutosave();
    });
  }

  // In-wizard Back button. Drives the BROWSER history backward (history.back),
  // which fires our popstate handler -> applyHistoryStep. This keeps the on-page
  // Back button and the browser/gesture Back perfectly in sync (one source of
  // truth). Falls back to a direct state change if the History API is absent.
  back() {
    if (this.state.step <= STEP_SERVICE) return; // nothing to go back to
    if (typeof window !== 'undefined' && window.history) {
      window.history.back();
      return;
    }
    // SSR / no-History fallback: step back directly.
    this.applyHistoryStep(Math.max(0, this.state.step - 1));
  }

  // ---- submit ----

  buildIntakeAnswers() {
    const { questions, answers, photos } = this.state;
    const out = {};
    questions.forEach((q) => {
      if (q.inputType === 'PHOTO') {
        const ps = photos[q.id];
        if (Array.isArray(ps) && ps.length) out[q.id] = ps.map((p) => p.url);
      } else if (answers[q.id] != null && !(Array.isArray(answers[q.id]) && answers[q.id].length === 0) && String(answers[q.id]) !== '') {
        out[q.id] = answers[q.id];
      }
    });
    return out;
  }

  // Submit entry point. Re-runs every gate (photos, measurements, project), then
  // GATES on auth: logged-in -> publish now; logged-out -> open the inline auth
  // panel (all answers/photos/measures are preserved in state).
  submit() {
    const pErr = this.validatePhotos();
    if (pErr) { this.setState({ error: pErr, step: STEP_PHOTOS }); return; }
    const mErr = this.validateMeasure();
    if (mErr) { this.setState({ error: mErr, step: STEP_MEASURE }); return; }
    const err = this.validateProject();
    if (err) { this.setState({ error: err, step: STEP_PROJECT }); return; }
    if (!this.isAuthed()) {
      this.setState({ authOpen: true, authPhase: 'phone', authError: '', authNotice: '', otp: '' });
      return;
    }
    this.doSubmit();
  }

  // ---- inline auth (phone + SMS OTP), mirrored from CompanyAuth.js ----------
  // userType 1 = customer/client.
  setAuthField(field, value) {
    this.setState({ [field]: value, authError: '' });
  }

  authPhoneValid() {
    return (this.state.phoneNumber || '').replace(/\D/g, '').length >= 7;
  }

  authDetailsValid() {
    const { authEmail, authFirstName } = this.state;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((authEmail || '').trim());
    return (authFirstName || '').trim().length >= 1 && emailOk && this.authPhoneValid();
  }

  // Send the SMS verification code (jwtToken '' for logged-out callers, like the
  // client login action and CompanyAuth).
  async sendAuthOtp() {
    if (!this.authDetailsValid()) {
      this.setState({ authError: 'Please enter your name, a valid email, and your mobile number.' });
      return;
    }
    const { phoneDialCode, phoneNumber } = this.state;
    this.setState({ authSaving: true, authError: '', authNotice: '' });
    try {
      const res = await axios.post('/graphql', {
        query: sendVerificationSecureSms.loc.source.body,
        variables: { phoneDialCode, phoneNumber: phoneNumber.trim(), userType: 1, jwtToken: encode(clientSmsKey) },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.sendVerificationSecureSms;
      if (env && env.status === 200) {
        this.setState({ authSaving: false, authPhase: 'otp', authNotice: 'We texted you a verification code.' });
      } else {
        this.setState({ authSaving: false, authError: (env && env.errorMessage) || 'Could not send the code. Check the number and try again.' });
      }
    } catch (e) {
      this.setState({ authSaving: false, authError: `Could not send the code: ${e.message}` });
    }
  }

  // Verify the OTP. Existing user -> result.token. New user (result.auth === '')
  // -> finalize with createUser. On success we set the id_token cookie and submit.
  async verifyAuthOtp() {
    const { phoneDialCode, phoneNumber, otp } = this.state;
    this.setState({ authSaving: true, authError: '', authNotice: '' });
    try {
      const res = await axios.post('/graphql', {
        query: verifyPhoneNumber.loc.source.body,
        variables: { phoneDialCode, phoneNumber: phoneNumber.trim(), verificationCode: Number(otp), userType: 1 },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.verifyPhoneNumber;
      if (!env || env.status !== 200) {
        this.setState({ authSaving: false, authError: (env && env.errorMessage) || 'That code didn’t work. Please try again.' });
        return;
      }
      const result = env.result || {};
      if (result.token) {
        // Existing account — log straight in and submit.
        this.finishAuth(result.token, result.expiresIn);
        return;
      }
      // No account yet — create the client user, then submit.
      await this.createClientUser();
    } catch (e) {
      this.setState({ authSaving: false, authError: `Verification failed: ${e.message}` });
    }
  }

  async createClientUser() {
    const { authFirstName, authLastName, authEmail, phoneDialCode, phoneNumber, acquisitionSource } = this.state;
    try {
      const res = await axios.post('/graphql', {
        query: createUser,
        variables: {
          firstName: (authFirstName || '').trim(),
          lastName: (authLastName || '').trim(),
          email: authEmail.trim(),
          phoneDialCode,
          phoneNumber: phoneNumber.trim(),
          phoneCountryCode: 'US',
          userType: 1,
          acquisitionSource: acquisitionSource || null,
        },
      }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, withCredentials: true });
      const env = res.data && res.data.data && res.data.data.createUser;
      if (env && env.status === 200 && env.result && env.result.token) {
        this.finishAuth(env.result.token, env.result.expiresIn);
      } else if (env && env.status === 500) {
        // The server ALREADY has a valid session for us — a stale `id_token` cookie
        // the wizard couldn't read client-side (e.g. HttpOnly, set by a prior
        // small-jobs/admin login). createUser refuses with "already logged-in".
        // We ARE authenticated server-side, so don't dead-end: skip account
        // creation and submit the project with the existing session. If that
        // session is actually invalid, doSubmit() re-opens this auth panel.
        this.setState({ authSaving: false, authOpen: false, authError: '' });
        this.doSubmit();
      } else {
        this.setState({ authSaving: false, authError: (env && env.errorMessage) || 'We couldn’t create your account. Please try again.' });
      }
    } catch (e) {
      this.setState({ authSaving: false, authError: `Account creation failed: ${e.message}` });
    }
  }

  // Drop the id_token cookie (same mechanism as every web login) and immediately
  // run the real submit — the wizard answers are still in state, untouched.
  finishAuth(token, expiresIn) {
    setCookies({
      name: 'id_token',
      value: token,
      options: { path: '/', maxAge: 1000 * (expiresIn || 60 * 60 * 24 * 180) },
    });
    this.setState({ authSaving: false, authOpen: false, authNotice: '' }, () => this.doSubmit());
  }

  // Build the measurements JSON the API scores (numbers only; drop empties).
  buildMeasurementsJson() {
    const { measures } = this.state;
    const out = {};
    MEASURE_FIELDS.forEach((f) => {
      const raw = measures[f.key];
      if (raw != null && String(raw).trim() !== '') {
        const num = Number(raw);
        if (Number.isFinite(num)) out[f.key] = num;
      }
    });
    return JSON.stringify(out);
  }

  async doSubmit() {
    const err = this.validateProject();
    if (err) { this.setState({ error: err, step: STEP_PROJECT }); return; }
    this.setState({ submitting: true, error: '' });

    const { selection } = this.state;
    const zip = this.state.detailsZip || this.intakeZip();
    const category = this.categoryEnum();
    const title = `${selection.subCategoryName} — ${selection.categoryName}`.slice(0, 160);

    // 1) create the DRAFT project request — OR reuse the one progressively
    //    created during the wizard (W3b). Reusing the existing draftId avoids a
    //    duplicate DRAFT row; the subsequent updateProjectRequest overwrites the
    //    placeholder site fields with the real address. When no progressive
    //    DRAFT exists (guests, or create failed earlier) we create it here,
    //    exactly as before — the publish path is unchanged.
    let pr;
    if (this.state.draftId) {
      pr = { id: this.state.draftId };
    } else {
      const createRes = await lpApi(M_CREATE, {
        category,
        siteAddressLine1: (this.state.detailsAddress || '').trim() || 'To be confirmed',
        siteCity: this.state.detailsCity.trim(),
        siteState: this.state.detailsState,
        siteZip: String(zip).trim(),
        title,
      });
      const c = this.envelope(createRes, 'createProjectRequest');
      if (c.err) {
        // Stale/invalid cookie -> re-open the inline auth panel instead of erroring out.
        if (c.err === 'AUTH') {
          this.setState({ submitting: false, authOpen: true, authPhase: 'phone', authError: '', authNotice: '', otp: '' });
          return;
        }
        this.setState({ submitting: false, error: c.err });
        return;
      }
      pr = c.env.projectRequest;
    }
    if (!pr || !pr.id) {
      this.setState({ submitting: false, error: 'Could not create your project. Please try again.' });
      return;
    }
    const prId = String(pr.id);
    if (!this.state.draftId) this.setState({ draftId: prId });

    // 2) attach the intake answers + details + measurements
    const budget = BUDGET_BUCKETS.find((b) => b[0] === this.state.budgetKey);
    const timelineJson = JSON.stringify({ label: this.state.timeline });
    const categorySpecific = this.buildCategorySpecific();
    // A description is part of the server's universal score; fall back to a
    // generated one so the field is never empty.
    const description = (this.state.description || '').trim()
      || `${selection.subCategoryName} (${selection.categoryName}) — ${this.state.measures.areaSqFt || '?'} sq ft, ${this.state.timeline || 'flexible timeline'}.`;
    // Existing conditions + pro preferences blocks (each worth 5 server points).
    const existingConditions = JSON.stringify({
      areaSqFt: this.state.measures.areaSqFt || null,
      ceilingHeightFt: this.state.measures.ceilingHeightFt || null,
      propertyType: this.state.propertyType || null,
    });
    const proPreferences = JSON.stringify({
      budgetLabel: this.state.budgetKey || null,
      timeline: this.state.timeline || null,
      ambitionLevel: this.state.ambitionLevel || null,
    });
    const updateRes = await lpApi(M_UPDATE, {
      projectRequestId: prId,
      subcategory: selection.subCategoryName,
      description,
      propertyType: this.state.propertyType || null,
      ambitionLevel: this.state.ambitionLevel || null,
      budgetMin: budget && budget[1] != null ? Number(budget[1]) : null,
      budgetMax: budget && budget[2] != null ? Number(budget[2]) : null,
      timelineJson,
      categorySpecificDataJson: categorySpecific,
      intakeAnswersJson: JSON.stringify(this.buildIntakeAnswers()),
      measurementsJson: this.buildMeasurementsJson(),
      existingConditionsJson: existingConditions,
      proPreferencesJson: proPreferences,
    });
    const u = this.envelope(updateRes, 'updateProjectRequest');
    if (u.err === 'AUTH') {
      this.setState({ submitting: false, authOpen: true, authPhase: 'phone', authError: '', authNotice: '', otp: '' });
      return;
    }

    // 3) attach each guided photo to its slot (anti-circumvention checklist).
    const slotEntries = Object.keys(this.state.slotPhotos || {});
    for (let i = 0; i < slotEntries.length; i += 1) {
      const slotKey = slotEntries[i];
      const ph = this.state.slotPhotos[slotKey];
      if (ph && ph.url) {
        const isVideo = slotKey === 'video_360';
        // eslint-disable-next-line no-await-in-loop
        const aRes = await lpApi(M_ATTACH_MEDIA, {
          projectRequestId: prId,
          kind: isVideo ? 'VIDEO_360' : 'PHOTO',
          slot: slotKey,
          storageKey: ph.fileName || ph.url,
          cdnUrl: ph.url,
        });
        const a = this.envelope(aRes, 'attachProjectMedia');
        if (a.err === 'AUTH') {
          this.setState({ submitting: false, authOpen: true, authPhase: 'phone', authError: '', authNotice: '', otp: '' });
          return;
        }
        // Non-auth attach errors are non-fatal; publish will report any shortfall.
      }
    }

    // 4) publish — the server recomputes completeness, gates at 70, and (if
    //    enabled) charges the visit fee.
    const pubRes = await lpApi(M_PUBLISH, { projectRequestId: prId });
    const p = this.envelope(pubRes, 'publishProjectRequest');
    if (p.err === 'AUTH') {
      this.setState({ submitting: false, authOpen: true, authPhase: 'phone', authError: '', authNotice: '', otp: '' });
      return;
    }
    if (p.err) {
      // Blocked by the server (score < 70, no card for the visit fee, or a state
      // that is not yet open). Surface the reason + missing items and keep the
      // draft so the client can finish it. Bounce back to the photos step.
      const score = pubRes && pubRes.data && pubRes.data.publishProjectRequest
        ? pubRes.data.publishProjectRequest.completenessScore
        : null;
      const miss = (pubRes && pubRes.data && pubRes.data.publishProjectRequest
        && pubRes.data.publishProjectRequest.completenessMissing) || [];
      this.setState({
        submitting: false,
        created: pr,
        publishError: p.err,
        publishScore: score,
        publishMissing: miss,
        step: STEP_REVIEW,
      });
      return;
    }

    // Published — the DRAFT is now a live project, so drop the resume autosave
    // and add a success history entry (Back from success leaves the wizard).
    this.clearAutosave();
    this.setState({ submitting: false, created: pr, publishError: '', step: STEP_SUCCESS }, () => {
      this.pushStepHistory(STEP_SUCCESS, false);
    });
  }

  // ---- render helpers ----

  renderSteps() {
    const { step } = this.state;
    return (
      <div className={s.steps}>
        {STEPS.map((st, i) => (
          <React.Fragment key={st.key}>
            <div className={`${s.stepNode} ${i < step ? s.stepDone : ''} ${i === step ? s.stepActive : ''}`}>
              <span className={s.stepDot}>{i < step ? <Icon d={I_CHECK} /> : i + 1}</span>
              <span>{st.label}</span>
            </div>
            {i < STEPS.length - 1 && <span className={`${s.stepBar} ${i < step ? s.stepBarDone : ''}`} />}
          </React.Fragment>
        ))}
      </div>
    );
  }

  // AI entry at the very start of the wizard: a soft card with a textarea +
  // "Help me set this up" button. On result it pre-fills description, the
  // universal fields, budget/timeline, and a suggested-category hint — then the
  // homeowner just picks the matching service below. DRAFT ONLY.
  renderAiAssistant() {
    const { aiText, aiLoading, aiError, aiFollowUp, aiCategory, aiApplied } = this.state;
    const aiLpCat = aiCategory ? lpCategoryByValue(aiCategory) : null;
    const catLabel = aiLpCat ? aiLpCat.label : '';
    return (
      <div className={s.aiBox}>
        <div className={s.aiHead}>
          <span className={s.aiBadge}>AI</span>
          <div>
            <div className={s.aiTitle}>Describe your project in your own words</div>
            <div className={s.aiSub}>
              Tell us what you want done and we’ll set up the steps for you. You can edit everything after.
            </div>
          </div>
        </div>
        <textarea
          className={s.aiTextarea}
          placeholder="e.g. I want to fully renovate my 180 sq ft kitchen — new cabinets, countertops, and flooring. Mid-range finishes, hoping to start within a month."
          value={aiText}
          onChange={(e) => this.setState({ aiText: e.target.value, aiError: '' })}
          rows={4}
        />
        {aiApplied && (
          <div className={s.aiDone}>
            <Icon d={I_CHECK} />
            <span>
              Got it — we’ve set up your project
              {catLabel ? <> and selected the <strong>{catLabel}</strong> category</> : null}.
              Review each step and change anything that isn’t right.
            </span>
          </div>
        )}
        {aiFollowUp && (
          <div className={s.aiFollow}>{aiFollowUp}</div>
        )}
        {aiError && <div className={s.aiErr}>{aiError}</div>}
        <div className={s.aiActions}>
          <button
            type="button"
            className={`${s.btn} ${s.btnPrimary} ${s.aiBtn}`}
            onClick={() => this.runAiAssistant()}
            disabled={aiLoading}
          >
            {aiLoading
              ? <span className={s.aiSpinner} aria-hidden="true" />
              : null}
            {aiLoading ? 'Setting things up…' : (aiApplied ? 'Refine with more detail' : 'Help me set this up')}
          </button>
          <span className={s.aiHint}>Or just pick a category below — totally optional.</span>
        </div>
      </div>
    );
  }

  // LP-NATIVE category picker — premium tiles, one per large-project enum value.
  // The tile's value IS the enum sent to createProjectRequest (1:1). This is the
  // ONLY service-selection surface in the LP wizard; it does NOT read the
  // small-jobs Section/Category/SubCategory catalog.
  renderService() {
    const { selection } = this.state;
    const selected = selection && selection.categoryValue;
    return (
      <div>
        <p className={s.fieldLabel}>What kind of large project is this?</p>
        <div className={s.grid}>
          {LP_CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              className={`${s.tile} ${selected === cat.value ? s.tileOn : ''}`}
              onClick={() => this.selectCategory(cat.value)}
            >
              <span className={s.tileIcon}><Icon d={cat.icon} /></span>
              <span className={s.tileBody}>
                <span className={s.tileName}>{cat.label}</span>
                <span className={s.tileMeta}>
                  {cat.subs.length} option{cat.subs.length === 1 ? '' : 's'}
                </span>
              </span>
              <span style={{ marginLeft: 'auto', color: '#9aa0a6' }}><Icon d={I_CHEVRON} /></span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // LP-NATIVE intake step. Replaces the small-jobs categoryIntakeQuestions /
  // CategoryField questionnaire entirely. It offers a STATIC, LP-owned sub-option
  // list for the chosen category (LP_CATEGORIES[].subs) plus a free-text scope
  // note. No small-jobs Category/SubCategory/CategoryField is read here. The
  // remaining universal LP fields (property type / ambition / measurements /
  // budget / timeline) are collected in the dedicated later steps.
  renderIntake() {
    const { selection } = this.state;
    const lpCat = selection ? lpCategoryByValue(selection.categoryValue) : null;
    if (!lpCat) {
      return (
        <div className={s.centerState}>
          Choose a project type first, then we’ll ask a couple of quick questions.
        </div>
      );
    }
    const chosenSub = selection.subCategoryName;
    return (
      <div>
        <p className={s.fieldLabel}>Which best describes the work?</p>
        <div className={s.chips}>
          {lpCat.subs.map((sub) => (
            <button
              key={sub}
              type="button"
              className={`${s.chip} ${chosenSub === sub ? s.chipOn : ''}`}
              onClick={() => this.selectSubOption(sub)}
            >
              {sub}
            </button>
          ))}
        </div>

        <p className={s.fieldLabel} style={{ marginTop: 24 }}>Tell us about the project</p>
        <textarea
          className={s.textarea}
          placeholder={LP_SCOPE_PLACEHOLDER}
          value={this.state.description}
          onChange={(e) => this.setState({ description: e.target.value, error: '' })}
        />
        <div className={s.qHelp} style={{ marginTop: 6 }}>
          The more detail you give, the more accurately companies can quote your project without a site visit.
        </div>
      </div>
    );
  }

  // Live completeness bar + "what's left" checklist. Shown on the photos,
  // measurements, and review steps so the path to 70 is always visible.
  renderCompleteness() {
    const { score, missing } = this.completeness();
    const ok = score >= MIN_COMPLETENESS_SCORE;
    return (
      <div className={s.completeBox}>
        <div className={s.completeTop}>
          <span className={s.completeLabel}>Project completeness</span>
          <span className={`${s.completeScore} ${ok ? s.completeScoreOk : ''}`}>
            {score}/100
          </span>
        </div>
        <div className={s.completeTrack}>
          <span
            className={`${s.completeFill} ${ok ? s.completeFillOk : ''}`}
            style={{ width: `${score}%` }}
          />
          <span className={s.completeGate} style={{ left: `${MIN_COMPLETENESS_SCORE}%` }} />
        </div>
        <p className={s.completeHint}>
          {ok
            ? 'Great — your project is detailed enough to publish and get accurate quotes.'
            : `Reach ${MIN_COMPLETENESS_SCORE} to publish. A complete project lets companies quote without a site visit.`}
        </p>
        {!ok && missing.length > 0 && (
          <ul className={s.completeList}>
            {missing.slice(0, 5).map((m) => (
              <li key={m} className={s.completeItem}>
                <span className={s.completeDot} />
                {m}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // Guided photos step — labeled upload tiles, required ones flagged.
  renderPhotos() {
    const { photoSlots, slotsLoading, slotPhotos, slotUploads } = this.state;
    if (slotsLoading) {
      return <div className={s.centerState}>Loading the photo checklist…</div>;
    }
    if (!photoSlots.length) {
      return (
        <div className={s.centerState}>
          We couldn’t load the photo checklist. You can continue and add photos later.
        </div>
      );
    }
    const filledReq = photoSlots.filter((sl) => sl.required && slotPhotos[sl.key]).length;
    const totalReq = photoSlots.filter((sl) => sl.required).length;
    return (
      <div>
        <div className={s.photoSummary}>
          <span className={s.photoCount}>
            {this.guidedPhotoCount()}/{TARGET_PHOTO_COUNT} photos
          </span>
          <span className={s.photoReq}>
            {filledReq}/{totalReq} required shots
          </span>
        </div>
        {this.renderCompleteness()}
        <div className={s.slotGrid}>
          {photoSlots.map((sl) => {
            const up = slotUploads[sl.key] || {};
            const ph = slotPhotos[sl.key];
            const isPdf = ph && /\.pdf($|\?)/i.test(ph.url);
            return (
              <div key={sl.key} className={`${s.slotTile} ${ph ? s.slotTileDone : ''}`}>
                <div className={s.slotHead}>
                  <span className={s.slotLabel}>{sl.label}</span>
                  {sl.required
                    ? <span className={s.slotReq}>Required</span>
                    : <span className={s.slotOpt}>Recommended</span>}
                </div>
                {ph ? (
                  <div className={s.slotPreview}>
                    {isPdf
                      ? <div className={s.slotPdf}>{ph.name}</div>
                      : <img src={ph.url} alt={sl.label} />}
                    <button type="button" className={s.thumbX} onClick={() => this.removeSlotPhoto(sl.key)}>×</button>
                    <span className={s.slotDoneBadge}><Icon d={I_CHECK} /></span>
                  </div>
                ) : (
                  <div
                    className={s.slotDrop}
                    role="button"
                    tabIndex={0}
                    onClick={() => this.fileInputs[`slot_${sl.key}`] && this.fileInputs[`slot_${sl.key}`].click()}
                    onKeyDown={(e) => { if (e.key === 'Enter' && this.fileInputs[`slot_${sl.key}`]) this.fileInputs[`slot_${sl.key}`].click(); }}
                  >
                    <div className={s.uploadIcon}><Icon d={I_UPLOAD} /></div>
                    <div className={s.uploadHint}>{up.uploading ? 'Uploading…' : 'Add photo'}</div>
                  </div>
                )}
                <input
                  ref={(el) => { this.fileInputs[`slot_${sl.key}`] = el; }}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    this.uploadSlotPhoto(sl.key, f);
                    e.target.value = '';
                  }}
                />
                {up.error && <div className={s.fieldErr}>{up.error}</div>}
              </div>
            );
          })}
        </div>
        {this.renderPhoneHandoff()}
      </div>
    );
  }

  // "Add photos from your phone" — QR handoff for desktop clients with no camera.
  renderPhoneHandoff() {
    const {
      handoffOpen, handoffLoading, handoffError, handoffUrl, handoffQrSvg, handoffCount,
    } = this.state;
    return (
      <div className={s.handoffPanel}>
        {!handoffOpen ? (
          <button
            type="button"
            className={s.handoffOpenBtn}
            onClick={() => this.openHandoff()}
          >
            <span className={s.handoffPhone} aria-hidden>📱</span>
            Add photos from your phone
          </button>
        ) : (
          <div className={s.handoffOpen}>
            <div className={s.handoffHead}>
              <span className={s.handoffTitle}>Add photos from your phone</span>
              <button
                type="button"
                className={s.handoffClose}
                onClick={() => this.closeHandoff()}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {handoffLoading ? (
              <div className={s.handoffBody}>Setting up a secure code…</div>
            ) : handoffError ? (
              <div className={s.handoffBody}>
                <div className={s.fieldErr} style={{ marginBottom: 12 }}>{handoffError}</div>
                <button type="button" className={s.handoffRetry} onClick={() => { this.setState({ handoffOpen: false }, () => this.openHandoff()); }}>
                  Try again
                </button>
              </div>
            ) : (
              <div className={s.handoffBody}>
                <div className={s.handoffGrid}>
                  <div className={s.handoffQr}>
                    {handoffQrSvg ? (
                      // eslint-disable-next-line react/no-danger
                      <span dangerouslySetInnerHTML={{ __html: handoffQrSvg }} />
                    ) : (
                      <div className={s.handoffNoQr}>Open this link on your phone:</div>
                    )}
                  </div>
                  <div className={s.handoffText}>
                    <p className={s.handoffLead}>Scan with your phone’s camera to take and add photos.</p>
                    <p className={s.handoffSmall}>
                      Photos taken on your phone appear here automatically. This code expires in 30 minutes.
                    </p>
                    {handoffUrl ? (
                      <a className={s.handoffLink} href={handoffUrl} target="_blank" rel="noopener noreferrer">
                        {handoffUrl}
                      </a>
                    ) : null}
                    {handoffCount > 0 ? (
                      <div className={s.handoffCount}>
                        <span className={s.handoffCheck} aria-hidden>✓</span>
                        {handoffCount} {handoffCount === 1 ? 'photo' : 'photos'} added from your phone
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Measurements step — area + ceiling height + property type (required) +
  // ambition + optional measures.
  renderMeasure() {
    const { measures, propertyType, ambitionLevel } = this.state;
    return (
      <div>
        {this.renderCompleteness()}
        <div className={s.measureGrid}>
          {MEASURE_FIELDS.map((f) => (
            <div className={s.measureField} key={f.key}>
              <p className={s.fieldLabel}>
                {f.label}
                {f.required && <span className={s.qReq}>*</span>}
              </p>
              <input
                className={s.input}
                type="text"
                inputMode="decimal"
                placeholder={f.placeholder}
                value={measures[f.key] || ''}
                onChange={(e) => this.setMeasure(f.key, e.target.value)}
              />
              {f.hint && <div className={s.qHelp} style={{ marginTop: 6 }}>{f.hint}</div>}
            </div>
          ))}
        </div>

        <p className={s.fieldLabel} style={{ marginTop: 24 }}>Property type<span className={s.qReq}>*</span></p>
        <div className={s.chips}>
          {PROPERTY_TYPES.map(([label, val]) => (
            <button
              key={val}
              type="button"
              className={`${s.chip} ${propertyType === val ? s.chipOn : ''}`}
              onClick={() => this.setState({ propertyType: propertyType === val ? '' : val, error: '' })}
            >
              {label}
            </button>
          ))}
        </div>

        <p className={s.fieldLabel} style={{ marginTop: 24 }}>Finish level</p>
        <div className={s.chips}>
          {AMBITION_LEVELS.map(([label, val]) => (
            <button
              key={val}
              type="button"
              className={`${s.chip} ${ambitionLevel === val ? s.chipOn : ''}`}
              onClick={() => this.setState({ ambitionLevel: ambitionLevel === val ? '' : val, error: '' })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---- Google Places address autocomplete (street-address field) ----------
  // Talks ONLY to our same-origin server proxy (/lp/places/*), which holds the server key. When no
  // key is configured the proxy returns empty lists, so the dropdown never opens and the field is a
  // plain manual input. Client-only (guarded by typeof window) so SSR never fetches.
  onAddressChange = (value) => {
    this.setState({ detailsAddress: value, error: '' });
    if (typeof window === 'undefined') return;
    if (this._addrTimer) clearTimeout(this._addrTimer);
    const q = (value || '').trim();
    if (q.length < 3) {
      this.setState({ addrSuggestions: [], addrSuggestOpen: false, addrSuggestLoading: false });
      return;
    }
    this._addrTimer = setTimeout(() => this.fetchAddressSuggestions(q), 250);
  };

  fetchAddressSuggestions = (q) => {
    const reqId = (this._addrReq = (this._addrReq || 0) + 1);
    this.setState({ addrSuggestLoading: true });
    fetch(`/lp/places/autocomplete?q=${encodeURIComponent(q)}`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((data) => {
        // Ignore out-of-order responses (debounced typing can race).
        if (reqId !== this._addrReq) return;
        const suggestions = (data && data.suggestions) || [];
        this.setState({
          addrSuggestions: suggestions,
          addrSuggestOpen: suggestions.length > 0,
          addrSuggestLoading: false,
        });
      })
      .catch(() => {
        if (reqId !== this._addrReq) return;
        // Graceful: behave as a plain field on any failure.
        this.setState({ addrSuggestions: [], addrSuggestOpen: false, addrSuggestLoading: false });
      });
  };

  onSelectAddressSuggestion = (sugg) => {
    if (!sugg || !sugg.placeId) return;
    // Show the chosen text immediately, close the dropdown.
    this.setState({
      detailsAddress: sugg.description || this.state.detailsAddress,
      addrSuggestions: [],
      addrSuggestOpen: false,
      error: '',
    });
    if (typeof window === 'undefined') return;
    fetch(`/lp/places/details?placeId=${encodeURIComponent(sugg.placeId)}`, { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => {
        if (!d) return;
        const next = {};
        // Prefer the parsed street line; fall back to the suggestion text already shown.
        if (d.address) next.detailsAddress = d.address;
        // Only overwrite city/state/zip when details actually returned them.
        if (d.city) next.detailsCity = d.city;
        if (d.state && /^[A-Z]{2}$/.test(d.state) && US_STATES.indexOf(d.state) !== -1) next.detailsState = d.state;
        if (d.zip) next.detailsZip = String(d.zip).replace(/[^\d]/g, '').slice(0, 5);
        if (Object.keys(next).length) this.setState(next);
      })
      .catch(() => {});
  };

  closeAddressSuggest = () => this.setState({ addrSuggestOpen: false });

  renderProject() {
    const { detailsZip, detailsCity, detailsState, detailsAddress, budgetKey, timeline, acquisitionSource, addrSuggestions, addrSuggestOpen } = this.state;
    const prefilledZip = this.intakeZip();
    return (
      <div>
        <p className={s.fieldLabel}>Where is the project?</p>
        <div className={s.twoCol}>
          <div className={s.addrField}>
            <input
              className={s.input}
              type="text"
              placeholder="Street address"
              autoComplete="off"
              value={detailsAddress}
              onChange={(e) => this.onAddressChange(e.target.value)}
              onFocus={() => { if (addrSuggestions && addrSuggestions.length) this.setState({ addrSuggestOpen: true }); }}
              onBlur={() => { setTimeout(this.closeAddressSuggest, 150); }}
              onKeyDown={(e) => { if (e.key === 'Escape') this.closeAddressSuggest(); }}
              role="combobox"
              aria-expanded={addrSuggestOpen}
              aria-autocomplete="list"
            />
            {addrSuggestOpen && addrSuggestions && addrSuggestions.length > 0 && (
              <ul className={s.addrDropdown} role="listbox">
                {addrSuggestions.map((sg) => (
                  <li
                    key={sg.placeId}
                    role="option"
                    className={s.addrOption}
                    // onMouseDown (not onClick) so the selection fires before the input's onBlur closes it.
                    onMouseDown={(e) => { e.preventDefault(); this.onSelectAddressSuggestion(sg); }}
                  >
                    {sg.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <input
              className={s.input}
              type="text"
              placeholder="City"
              value={detailsCity}
              onChange={(e) => this.setState({ detailsCity: e.target.value, error: '' })}
            />
          </div>
        </div>
        <div className={s.twoCol} style={{ marginTop: 12 }}>
          <div>
            <select
              className={s.select}
              value={detailsState}
              onChange={(e) => this.setState({ detailsState: e.target.value, error: '' })}
            >
              <option value="">State…</option>
              {US_STATES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div>
            <input
              className={s.input}
              type="text"
              inputMode="numeric"
              maxLength={5}
              placeholder={prefilledZip ? `ZIP (${prefilledZip})` : 'ZIP code'}
              value={detailsZip}
              onChange={(e) => this.setState({ detailsZip: e.target.value.replace(/[^\d]/g, '').slice(0, 5), error: '' })}
            />
          </div>
        </div>

        <p className={s.fieldLabel} style={{ marginTop: 24 }}>What is your budget?</p>
        <div className={s.chips}>
          {BUDGET_BUCKETS.map((b) => (
            <button
              key={b[0]}
              type="button"
              className={`${s.chip} ${budgetKey === b[0] ? s.chipOn : ''}`}
              onClick={() => this.setState({ budgetKey: budgetKey === b[0] ? '' : b[0], error: '' })}
            >
              {b[0]}
            </button>
          ))}
        </div>

        <p className={s.fieldLabel} style={{ marginTop: 24 }}>When do you want to start?</p>
        <div className={s.chips}>
          {TIMELINE_BUCKETS.map((t) => (
            <button
              key={t}
              type="button"
              className={`${s.chip} ${timeline === t ? s.chipOn : ''}`}
              onClick={() => this.setState({ timeline: timeline === t ? '' : t, error: '' })}
            >
              {t}
            </button>
          ))}
        </div>

        <p className={s.fieldLabel} style={{ marginTop: 24 }}>How did you hear about us? (optional)</p>
        <select
          className={s.select}
          value={acquisitionSource}
          onChange={(e) => this.setState({ acquisitionSource: e.target.value })}
        >
          <option value="">Select an option…</option>
          {ACQUISITION_OPTIONS.map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
      </div>
    );
  }

  renderReview() {
    const { selection, budgetKey, timeline } = this.state;
    const zip = this.state.detailsZip || this.intakeZip();
    const guided = this.guidedPhotoCount();
    const { visitFeeAmount, publishError, publishScore, publishMissing } = this.state;
    return (
      <div>
        {this.renderCompleteness()}

        {publishError && (
          <div className={s.errorBox}>
            <strong>We couldn’t publish your project yet.</strong>
            <div style={{ marginTop: 6, fontWeight: 600 }}>{publishError}</div>
            {publishScore != null && (
              <div style={{ marginTop: 4 }}>Current score: {publishScore}/100 (need {MIN_COMPLETENESS_SCORE}).</div>
            )}
            {Array.isArray(publishMissing) && publishMissing.length > 0 && (
              <ul className={s.completeList} style={{ marginTop: 8 }}>
                {publishMissing.slice(0, 6).map((m) => (
                  <li key={m} className={s.completeItem}><span className={s.completeDot} />{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {visitFeeAmount != null && visitFeeAmount > 0 && (
          <div className={s.noticeBox}>
            A one-time site visit fee of ${Number(visitFeeAmount).toFixed(2)} applies when you publish.
            Make sure a payment card is on file — you’ll be prompted if one is missing.
          </div>
        )}

        <div className={s.reviewBlock}>
          <div className={s.reviewHead}>
            <h4>Project type</h4>
            <button type="button" className={s.crumbBtn} onClick={() => this.setState({ step: STEP_SERVICE })}>Edit</button>
          </div>
          <div className={s.reviewRow}><span className={s.reviewKey}>Category</span><span className={s.reviewVal}>{selection.categoryName}</span></div>
          {selection.subCategoryName && selection.subCategoryName !== selection.categoryName && (
            <div className={s.reviewRow}><span className={s.reviewKey}>Focus</span><span className={s.reviewVal}>{selection.subCategoryName}</span></div>
          )}
        </div>

        {this.state.description && this.state.description.trim() && (
          <div className={s.reviewBlock}>
            <div className={s.reviewHead}>
              <h4>Project details</h4>
              <button type="button" className={s.crumbBtn} onClick={() => this.setState({ step: STEP_INTAKE })}>Edit</button>
            </div>
            <div className={s.reviewRow}>
              <span className={s.reviewVal} style={{ whiteSpace: 'pre-wrap' }}>{this.state.description.trim()}</span>
            </div>
          </div>
        )}

        <div className={s.reviewBlock}>
          <div className={s.reviewHead}>
            <h4>Guided photos & measurements</h4>
            <button type="button" className={s.crumbBtn} onClick={() => this.setState({ step: STEP_PHOTOS })}>Edit</button>
          </div>
          <div className={s.reviewRow}>
            <span className={s.reviewKey}>Guided photos</span>
            <span className={s.reviewVal}>{guided}/{TARGET_PHOTO_COUNT}</span>
          </div>
          <div className={s.reviewRow}>
            <span className={s.reviewKey}>Work area</span>
            <span className={s.reviewVal}>{this.state.measures.areaSqFt ? `${this.state.measures.areaSqFt} sq ft` : '—'}</span>
          </div>
          <div className={s.reviewRow}>
            <span className={s.reviewKey}>Ceiling height</span>
            <span className={s.reviewVal}>{this.state.measures.ceilingHeightFt ? `${this.state.measures.ceilingHeightFt} ft` : '—'}</span>
          </div>
        </div>

        <div className={s.reviewBlock}>
          <div className={s.reviewHead}>
            <h4>Budget, timeline & location</h4>
            <button type="button" className={s.crumbBtn} onClick={() => this.setState({ step: STEP_PROJECT })}>Edit</button>
          </div>
          <div className={s.reviewRow}><span className={s.reviewKey}>Budget</span><span className={s.reviewVal}>{budgetKey || '—'}</span></div>
          <div className={s.reviewRow}><span className={s.reviewKey}>Timeline</span><span className={s.reviewVal}>{timeline || '—'}</span></div>
          <div className={s.reviewRow}><span className={s.reviewKey}>Location</span><span className={s.reviewVal}>{[this.state.detailsCity, this.state.detailsState, zip].filter(Boolean).join(', ') || '—'}</span></div>
        </div>
      </div>
    );
  }

  renderSuccess() {
    const { created } = this.state;
    return (
      <div className={s.success}>
        <div className={s.successIcon}><Icon d={I_CHECK} /></div>
        <h2 className={s.successTitle}>Your project is published</h2>
        <p className={s.successText}>
          Thanks! Your project{created && created.id ? ` (#${created.id})` : ''} is now live for our
          verified companies to quote — with identities masked until you choose. You can track quotes and
          messages anytime from your account.
        </p>
        <div className={s.footer} style={{ justifyContent: 'center' }}>
          <button
            type="button"
            className={`${s.btn} ${s.btnGhost}`}
            onClick={() => this.goTo('/')}
          >
            Back to home
          </button>
          <button
            type="button"
            className={`${s.btn} ${s.btnPrimary}`}
            onClick={() => this.goTo('/my-projects')}
          >
            Track quotes
          </button>
        </div>
      </div>
    );
  }

  // Inline auth panel shown ONLY at submit for logged-out clients. Uses the exact
  // phone + SMS-OTP mechanism the rest of the web uses (createUser / verifyPhoneNumber).
  renderAuthPanel() {
    const {
      authPhase, authSaving, authError, authNotice,
      authFirstName, authLastName, authEmail, phoneDialCode, phoneNumber, otp,
    } = this.state;
    return (
      <div className={s.authPanel}>
        <h3 className={s.authTitle}>Almost done — create your account to post</h3>
        <p className={s.authHint}>
          Your project details are saved. We just need a quick account so pros can reach you and you
          can track your request. We’ll text you a verification code — no password needed.
        </p>

        {authError && <div className={s.errorBox}>{authError}</div>}
        {authNotice && <div className={s.noticeBox}>{authNotice}</div>}

        {authPhase === 'phone' ? (
          <div>
            <div className={s.twoCol}>
              <div>
                <p className={s.fieldLabel}>First name *</p>
                <input
                  className={s.input}
                  type="text"
                  value={authFirstName}
                  placeholder="Jordan"
                  onChange={(e) => this.setAuthField('authFirstName', e.target.value)}
                />
              </div>
              <div>
                <p className={s.fieldLabel}>Last name</p>
                <input
                  className={s.input}
                  type="text"
                  value={authLastName}
                  placeholder="Rivera"
                  onChange={(e) => this.setAuthField('authLastName', e.target.value)}
                />
              </div>
            </div>
            <p className={s.fieldLabel} style={{ marginTop: 12 }}>Email *</p>
            <input
              className={s.input}
              type="email"
              value={authEmail}
              placeholder="you@email.com"
              onChange={(e) => this.setAuthField('authEmail', e.target.value)}
            />
            <div className={s.twoCol} style={{ marginTop: 12 }}>
              <div style={{ maxWidth: 120 }}>
                <p className={s.fieldLabel}>Dial code</p>
                <input
                  className={s.input}
                  type="text"
                  value={phoneDialCode}
                  placeholder="+1"
                  onChange={(e) => this.setAuthField('phoneDialCode', e.target.value)}
                />
              </div>
              <div>
                <p className={s.fieldLabel}>Mobile phone *</p>
                <input
                  className={s.input}
                  type="tel"
                  value={phoneNumber}
                  placeholder="555 123 4567"
                  onChange={(e) => this.setAuthField('phoneNumber', e.target.value)}
                />
              </div>
            </div>
            <div className={s.footer}>
              <button
                type="button"
                className={`${s.btn} ${s.btnGhost}`}
                onClick={() => this.setState({ authOpen: false, authError: '', authNotice: '' })}
                disabled={authSaving}
              >
                Back
              </button>
              <button
                type="button"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={() => this.sendAuthOtp()}
                disabled={authSaving || !this.authDetailsValid()}
              >
                {authSaving ? 'Sending code…' : 'Send verification code'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className={s.fieldLabel}>
              Enter the code we sent to {phoneDialCode} {phoneNumber}
            </p>
            <input
              className={`${s.input} ${s.zip}`}
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={otp}
              placeholder="4-digit code"
              onChange={(e) => this.setAuthField('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <div className={s.footer}>
              <button
                type="button"
                className={`${s.btn} ${s.btnGhost}`}
                onClick={() => this.setState({ authPhase: 'phone', authError: '', authNotice: '', otp: '' })}
                disabled={authSaving}
              >
                Back
              </button>
              <button
                type="button"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={() => this.verifyAuthOtp()}
                disabled={authSaving || (otp || '').length < 4}
              >
                {authSaving ? 'Verifying…' : 'Verify & post my project'}
              </button>
            </div>
            <button
              type="button"
              className={s.crumbBtn}
              style={{ marginTop: 10 }}
              onClick={() => this.sendAuthOtp()}
              disabled={authSaving}
            >
              Resend code
            </button>
          </div>
        )}
      </div>
    );
  }

  // Premium Large Projects landing — the default view before the wizard starts.
  // Mirrors CompanyAuth's structure (hero value-prop + trust row, benefit cards,
  // "How it works", primary CTA). The CTA flips `started` -> reveals the wizard.
  renderLanding() {
    return (
      <div className={s.page}>
        <section className={s.hero}>
          <div className={s.wrap}>
            <span className={s.eyebrow}>BestHand Large Projects</span>
            <h1 className={s.h1}>
              Got a big job? Get competitive quotes from{' '}
              <span className={s.hl}>verified, escrow-protected companies</span>.
            </h1>
            <p className={s.sub}>
              Renovations, additions, and multi-trade projects — describe yours once and get matched
              with vetted companies. Compare quotes with identities masked until you choose, then pay
              safely through escrow milestones.
            </p>
            <div className={s.ctaRow}>
              <button
                type="button"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={() => this.startWizard()}
              >
                Request a quote for large project
              </button>
            </div>
            <div className={s.trustRow}>
              {TRUST_SIGNALS.map((t) => (
                <span className={s.trustItem} key={t.label}>
                  <Icon d={t.d} />
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className={s.valueWrap}>
          <div className={s.wrap}>
            <div className={s.valueGrid}>
              {VALUE_PROPS.map((v) => (
                <div className={s.valueCard} key={v.title}>
                  <div className={s.valueIc}><Icon d={v.d} /></div>
                  <h3 className={s.valueTitle}>{v.title}</h3>
                  <p className={s.valueBody}>{v.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={s.howWrap}>
          <div className={s.wrap}>
            <h2 className={s.howTitle}>How it works</h2>
            <div className={s.howGrid}>
              {STEPS_HOW.map((h) => (
                <div className={s.howCard} key={h.n}>
                  <span className={s.howNo}>{h.n}</span>
                  <h3 className={s.howCardTitle}>{h.title}</h3>
                  <p className={s.howCardBody}>{h.body}</p>
                </div>
              ))}
            </div>
            <div className={s.howCta}>
              <button
                type="button"
                className={`${s.btn} ${s.btnPrimary}`}
                onClick={() => this.startWizard()}
              >
                Request a quote for large project
              </button>
              <a className={s.btnText} href="/become-a-company">
                Are you a company? Join here
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // "Continue your project" — shown when a signed-in client has a newest
  // in-progress DRAFT (W3b). Inline so it works on both the landing and inside
  // the wizard. Uses existing button styles; no invented CSS class.
  renderResumeBanner() {
    const { resumeOffer } = this.state;
    if (!resumeOffer) return null;
    const sel = resumeOffer.selection;
    const label = sel && sel.subCategoryName
      ? `${sel.subCategoryName}${sel.categoryName ? ` · ${sel.categoryName}` : ''}`
      : 'your project';
    return (
      <div className={s.noticeBox} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span>You have an unfinished project ({label}). Continue where you left off?</span>
        <span style={{ display: 'flex', gap: 8 }}>
          <button type="button" className={`${s.btn} ${s.btnGhost}`} onClick={() => this.dismissResume()}>
            Start fresh
          </button>
          <button type="button" className={`${s.btn} ${s.btnPrimary}`} onClick={() => this.acceptResume()}>
            Continue your project
          </button>
        </span>
      </div>
    );
  }

  render() {
    const { started, loading, step, error, submitting, authOpen } = this.state;

    if (!started) {
      return (
        <div>
          {this.state.resumeOffer && (
            <div className={s.wrap} style={{ paddingTop: 16 }}>{this.renderResumeBanner()}</div>
          )}
          {this.renderLanding()}
        </div>
      );
    }

    return (
      <div className={s.page}>
        <section className={s.hero}>
          <div className={s.wrap}>
            <span className={s.eyebrow}>Post a Project</span>
            <h1 className={s.h1}>
              Tell us about your project, <span className={s.hl}>get matched with pros</span>
            </h1>
            <p className={s.sub}>
              Pick a service, answer a few quick questions, set your budget and timeline. It is free to post.
            </p>
          </div>
        </section>

        <section className={s.body}>
          <div className={s.wrap}>
            {this.renderResumeBanner()}
            {loading ? (
              <div className={s.loading}>Loading…</div>
            ) : step === STEP_SUCCESS ? (
              <div className={s.card}>{this.renderSuccess()}</div>
            ) : (
              <div>
                {this.renderSteps()}
                <div className={s.card}>
                  {error && <div className={s.errorBox}>{error}</div>}

                  {step === STEP_SERVICE && (
                    <div>
                      <h2 className={s.cardTitle}>What kind of large project is this?</h2>
                      <p className={s.cardSub}>Describe your project below and we’ll set it up — or pick the category that fits best.</p>
                      {this.renderAiAssistant()}
                      {this.renderService()}
                    </div>
                  )}
                  {step === STEP_INTAKE && (
                    <div>
                      <h2 className={s.cardTitle}>A few quick questions</h2>
                      <p className={s.cardSub}>
                        {this.state.selection ? `${this.state.selection.subCategoryName} · ${this.state.selection.categoryName}` : ''}
                      </p>
                      {this.renderIntake()}
                    </div>
                  )}
                  {step === STEP_PHOTOS && (
                    <div>
                      <h2 className={s.cardTitle}>Guided photos</h2>
                      <p className={s.cardSub}>
                        Follow the checklist so a company can quote your project accurately — without a site visit.
                        Required shots are marked; aim for at least {TARGET_PHOTO_COUNT}.
                      </p>
                      {this.renderPhotos()}
                    </div>
                  )}
                  {step === STEP_MEASURE && (
                    <div>
                      <h2 className={s.cardTitle}>Measurements</h2>
                      <p className={s.cardSub}>
                        A few measurements let companies price materials and labor precisely, sight unseen.
                      </p>
                      {this.renderMeasure()}
                    </div>
                  )}
                  {step === STEP_PROJECT && (
                    <div>
                      <h2 className={s.cardTitle}>Budget, timeline & location</h2>
                      <p className={s.cardSub}>This helps us match you with the right pros.</p>
                      {this.renderProject()}
                    </div>
                  )}
                  {step === STEP_REVIEW && !authOpen && (
                    <div>
                      <h2 className={s.cardTitle}>Review &amp; publish</h2>
                      <p className={s.cardSub}>
                        Your project must reach {MIN_COMPLETENESS_SCORE}/100 to publish. Review everything, then post.
                      </p>
                      {this.renderReview()}
                    </div>
                  )}
                  {step === STEP_REVIEW && authOpen && this.renderAuthPanel()}

                  {!(step === STEP_REVIEW && authOpen) && (
                    <div className={s.footer}>
                      <button
                        type="button"
                        className={`${s.btn} ${s.btnGhost}`}
                        onClick={() => this.back()}
                        disabled={step === STEP_SERVICE || submitting}
                      >
                        Back
                      </button>
                      {step < STEP_REVIEW ? (
                        <button type="button" className={`${s.btn} ${s.btnPrimary}`} onClick={() => this.next()}>
                          Continue
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`${s.btn} ${s.btnPrimary}`}
                          onClick={() => this.submit()}
                          disabled={submitting}
                        >
                          {submitting ? 'Publishing…' : 'Publish my project'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
}

export default withStyles(s)(PostAProject);
